import {Injectable, Optional, Type} from "@angular/core";

import {CostTrendAggregation, CostTrendFlat} from "app/billing-inventory/cost-variance-datatable/cost-variance-datatable.component";
import {FinopsDimensions, FinopsFocusDimension} from "app/finops/focus/finops-dimensions";
import {FinopsFocusComputing} from "app/finops/focus/finops-dimensions-computing";
import {CostMetricId, costMetricLookup} from "app/finops/focus/finops-dimensions-cost-metrics";
import {FinopsFocusGrouping} from "app/finops/focus/finops-dimensions-grouping";
import {FinopsFocusMapping} from "app/finops/focus/finops-dimensions-mapping";
import {ApiService} from "app/services/domain/api.service";
import {BillingSourceName, BillingSourceType} from "app/services/domain/billing/billing-source";
import {BillingGroupBy, BillingServiceAccountScope, granularitySeconds} from "app/services/domain/billing/billing-types";
import {LazyAccountTypeAggregationOptions, resolveLazyAccountTypeGrouping} from "app/services/domain/billing/lazy-account-type-grouping";
import {CloudProvidersService} from "app/services/domain/cloud-providers.service";
import {CurrencyConverter, CurrencyExchangePlanService} from "app/services/domain/currency-exchange.service";
import {DimensionExtended, DimensionsService} from "app/services/domain/dimensions/dimensions.service";
import type {AppDomainContext} from "app/services/domain/domain.module";
import {AccountGovernanceListService, HierarchyServiceAccountListRecordFetcher, ServiceAccountFetcher} from "app/services/domain/service-accounts/account-governance-list.service";
import {ServiceAccountHierarchyNode} from "app/services/domain/service-accounts/service-account-hierarchy";
import {ServiceAccountControlOption, ServiceAccountService} from "app/services/domain/service-accounts/service-account.service";
import {FilterOptionsExtended, ServiceAccountInfo, TenantInfo} from "app/services/domain/filter-options/filter-options.service";
import * as Models from "app/services/proxy/model/models";
import {PlatformTypeService} from "app/services/domain/platform-type.service";
import {GroupBySelector} from "app/services/ui/group-by-selector";
import {isAiPlatform, KubernetesWorkloadType, ServiceProviderName} from "app/services/ui/platform";
import {ForecastDuration, WidgetCostMetricStandaloneCondition, WidgetQueryBuilderAssistant} from "app/shared/filters/widget-filter/widget-filtering";
import {forecastRangeEnd, normalizeForecastPeriodStart} from "app/shared/forms/time-range/forecast-range";
import {RangeSelectionExtended} from "app/shared/forms/time-range/range-selection-extended";
import {SupportedBillingGroupBy} from "app/shared/query-group/group-by/billing-group-by-query-node";
import {findQueryOperator, findQueryOperatorByType, findQueryOperators, QueryOperatorLogicNode} from "app/shared/query-group/query-operator-query-node";

import {DedupeInterceptorStore, getPathSuffix} from "framework/services/dedupe-interceptor/dedupe-interceptor-store";
import {ErrorService} from "framework/services/error.service";
import {componentIdOf, inheritComponentId, withComponentId} from "framework/services/trace-headers-interceptor/trace-headers";
import {Lookup, LookupHelpers, UtilsService} from "framework/services/utils.service";
import {TimeRange, TimeRanges} from "framework/ui/charting/core/time";
import {ControlOption} from "framework/ui/control-option";
import {MomentHelper} from "framework/ui/formatting/date-format.pipe";
import {PercentagePipe, TitleCaseFormatPipe} from "framework/ui/formatting/string-format.pipe";
import {mapInParallel} from "framework/utils/concurrency";
import {Memoizer} from "framework/utils/memoizers";

export {BillingGroupBy, BillingServiceAccountScope, granularitySeconds} from "app/services/domain/billing/billing-types";

@Injectable()
export class BillingService
{
    public static readonly BILLING_EXTRAS_TTL: number      = 5 * 60_000;
    public static readonly LINE_ITEMS_SUMMARY_TTL: number  = 5 * 60_000;
    public static readonly BILLING_AGGREGATION_TTL: number = 0;
    public static readonly BILLING_TREND_TTL: number       = 0;

    static alignToHourBoundary(date: Date): Date
    {
        const m         = MomentHelper.parseAsUTC(date);
        const hourStart = m.clone()
                           .startOf("hour");
        return m.isSame(hourStart) ? m.toDate() : hourStart.add(1, "hour")
                                                           .toDate();
    }

    private static toUsageAggregation(result: BillingTrendResultExtended): BillingCostAndUsageAggregationResultExtended
    {
        let usage     = 0;
        let rateSum   = 0;
        let rateCount = 0;

        for (const point of result.trend.points)
        {
            usage += point.usage ?? 0;
            if (point.rate != null)
            {
                rateSum += point.rate;
                rateCount++;
            }
        }

        return new BillingCostAndUsageAggregationResultExtended(result.identity, result.trend.total, result.numAccounts, usage, rateCount > 0 ? rateSum / rateCount : 0);
    }

    private static forecastActualEndTime(rangeEndTime: number,
                                         now: number,
                                         isT2Range: boolean): number
    {
        return isT2Range ? rangeEndTime : now;
    }

    private static forecastSplitTime(rangeEndTime: number,
                                     now: number,
                                     isT2Range: boolean): number
    {
        return isT2Range ? rangeEndTime + 1 : now;
    }

    private static actualRequestEndTime(rangeEndTime: number,
                                        now: number,
                                        isT2Range: boolean): number
    {
        return isT2Range ? rangeEndTime : now;
    }

    // Cache to store the tag values for each key
    private readonly tagValuesCache: Map<string, Lookup<Set<string>>>                             = new Map();
    private readonly scopedTagValuesCache: Map<string, Promise<Models.ServiceAccountKeyValues[]>> = new Map();
    private readonly extraKeyValuesCache: Map<string, Lookup<Set<string>>>                        = new Map();
    private readonly k8sTagValuesCache: Map<string, Promise<Lookup<Lookup<Lookup<string[]>>>>>    = new Map();

    public costTrendApiStatus: string;

    constructor(readonly api: ApiService,
                readonly error: ErrorService,
                readonly currency: CurrencyExchangePlanService,
                readonly dimensions: DimensionsService,
                dedupeRegistry: DedupeInterceptorStore,
                @Optional() readonly platformType?: PlatformTypeService)
    {
        const pathPrefix = "/v2/billing/";
        dedupeRegistry.registerDedupeTtl((method: string,
                                          path: string) =>
                                         {
                                             if (method == "POST")
                                             {
                                                 path = getPathSuffix(path, pathPrefix);
                                                 switch (path)
                                                 {
                                                     case "aggregation":
                                                         return BillingService.BILLING_AGGREGATION_TTL;

                                                     case "aggregation/trend":
                                                         return BillingService.BILLING_TREND_TTL;

                                                     case "extras":
                                                         return BillingService.BILLING_EXTRAS_TTL;

                                                     case "line_items_summary":
                                                         return BillingService.LINE_ITEMS_SUMMARY_TTL;
                                                 }
                                             }
                                             return undefined;
                                         });
    }

    public getBillingSources(billingSource: string): string[]
    {
        if (this.isCustomerMargin(billingSource))
        {
            return [
                "customer_true_cost",
                "raw_cost"
            ];
        }
        else if (this.isPartnerMargin(billingSource))
        {
            return [
                "customer_true_cost",
                "partner_true_cost"
            ];
        }
        else
        {
            return [billingSource];
        }
    }

    public async trendIdentities(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                 currency: Models.Currency,
                                 context: Models.DataContext,
                                 rangeExt: RangeSelectionExtended): Promise<Models.BillingIdentitiesResult[]>
    {
        let resolvedFilterOptions: FilterOptionsExtended;
        resolvedFilterOptions ||= await Promise.resolve(filterOptions);
        const request = Models.BillingIdentitiesRequest.newInstance({
                                                                        service_account_ids: resolvedFilterOptions.filterServiceAccounts(context.organizationCurrency ? null : currency,
                                                                                                                                         null,
                                                                                                                                         null),
                                                                        filters            : context.query,
                                                                        start_date         : rangeExt.getMinAsDate(),
                                                                        end_date           : rangeExt.model.range
                                                                            ? BillingService.alignToHourBoundary(rangeExt.getMaxAsDate())
                                                                            : rangeExt.getMaxAsDate()
                                                                    });
        return await withComponentId(componentIdOf(context), () => this.api.billing.getBillingIdentities(request));
    }

    // Resolve choke point: callers forward unresolved so the await lands after the network fetch, not before it.
    private resolveFilterOptions(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>): Promise<FilterOptionsExtended>;
    private resolveFilterOptions(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended> | null): Promise<FilterOptionsExtended | null>;
    private resolveFilterOptions(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended> | null): Promise<FilterOptionsExtended | null>
    {
        return filterOptions == null ? Promise.resolve(null) : Promise.resolve(filterOptions);
    }

    public async costAggregation(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                 billingSource: string,
                                 groupBy: BillingGroupBy[],
                                 currency: Models.Currency = Models.Currency.USD,
                                 context: Models.DataContext,
                                 rangeExt: RangeSelectionExtended,
                                 options?: BillingAggregationOptions): Promise<BillingAggregationResult[]>
    {
        return this.costAggregationInternal(filterOptions,
                                            billingSource,
                                            groupBy,
                                            currency,
                                            context,
                                            rangeExt,
                                            options);
    }

    private async costAggregationInternal(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                          billingSource: string,
                                          groupBy: BillingGroupBy[],
                                          currency: Models.Currency = Models.Currency.USD,
                                          context: Models.DataContext,
                                          rangeExt: RangeSelectionExtended,
                                          options?: BillingAggregationOptions,
                                          skipK8ScopedCostComputation?: boolean): Promise<BillingAggregationResult[]>
    {
        const {
                  limit,
                  useRangeExtDate,
                  domain,
                  fetcher,
                  platformAsKubernetes,
                  platform,
                  computeK8ScopedCost,
                  isMultiCloudSaas
              } = options || {};

        try
        {
            const isMarginSource = this.isCostMetricMargin(billingSource);
            const billingSources = this.getBillingSources(billingSource);
            let query            = context.query;

            if (platformAsKubernetes && computeK8ScopedCost && !skipK8ScopedCostComputation && !isMarginSource)
            {
                return this.k8ScopedCostAggregation(filterOptions,
                                                    billingSource,
                                                    groupBy,
                                                    currency,
                                                    context,
                                                    rangeExt,
                                                    options);
            }

            if (context.organizationCurrency)
            {
                // Handle as a trend request
                const trendResult = await this.trendData(filterOptions,
                                                         billingSource,
                                                         groupBy,
                                                         currency,
                                                         context,
                                                         rangeExt,
                                                         Models.TrendGranularity.month,
                                                         {
                                                             limit,
                                                             domain,
                                                             platformAsKubernetes,
                                                             platform,
                                                             isMultiCloudSaas
                                                         });

                return trendResult.map(v => v.convertToAggregation());
            }

            if (!billingSource && WidgetCostMetricStandaloneCondition.isLoaded())
            {
                this.error.defaultError("You do not have access to any supported metrics required to display data on this dashboard. Please contact your administrator for access.");
            }

            const handler         = new BillingGroupByHandlerMulti(groupBy);
            const combinedGroupBy = handler.combinedGroupBy(true);

            const groups = combinedGroupBy.group_by;
            if (groups.length === 1)
            {
                const group = groups[0];
                if (group.selector == Models.BillingGroupBySelector.dimension && !group.key && !group.groups?.length) return [];
            }
            const groupProcessing = this.processBillingGroups(groups, groupBy, query, {handleHierarchy: true});
            query                 = groupProcessing.query;

            query = this.ensureNotOnboardedStatusForCspCustomers(query);
            query = WidgetQueryBuilderAssistant.adjustTagKeyQuery(groups, groupBy, query, groupProcessing.hasServiceAccountTags, groupProcessing.hasTenantTags);

            const startEnd = await this.getStartEnd(context, groupBy, rangeExt);
            query          = await this.processPlatformAccounts(query, platformAsKubernetes, domain, fetcher, platform);
            query          = this.appendCurrencyFilter(query, currency);

            const fetchAggregation = (source: string) =>
            {
                const aggregationRequest = Models.BillingAggregationRequest.newInstance({
                                                                                           billing_source: source,
                                                                                           filters       : query,
                                                                                           start_date    : useRangeExtDate ? rangeExt.getMinAsDate() : startEnd[0],
                                                                                           end_date      : rangeExt.model.range
                                                                                               ? BillingService.alignToHourBoundary(rangeExt.getMaxAsDate())
                                                                                               : rangeExt.getMaxAsDate(),
                                                                                           limit         : limit,
                                                                                           ...combinedGroupBy,
                                                                                           ...(platformAsKubernetes ? {platform: platform ?? Models.Platforms.kubernetes} : {})
                                                                                       });

                return withComponentId(componentIdOf(context), () => this.api.billing.getCostAggregation(aggregationRequest));
            };

            const includeRetailCost                                                       = isMarginSource && options?.includeRetailCost;
            const retailCostPromise: Promise<Models.BillingAggregationResult[] | null>    = includeRetailCost ? fetchAggregation("raw_list_cost") : Promise.resolve(null);
            const [values, retailCostRaw] = await Promise.all([
                                                                   mapInParallel(billingSources, fetchAggregation),
                                                                   retailCostPromise
                                                               ]);
            const resolvedFilterOptions = await this.resolveFilterOptions(filterOptions);
            if (values.length > 1 && isMarginSource)
            {
                const contractedCostValues = handler.groupResults(values[0].map(v => BillingAggregationResultExtended.newInstance(resolvedFilterOptions, v, groupBy)));
                const sourceCostValues     = handler.groupResults(values[1].map(v => BillingAggregationResultExtended.newInstance(resolvedFilterOptions, v, groupBy)));
                const retailCostValues     = retailCostRaw ? handler.groupResults(retailCostRaw.map(v => BillingAggregationResultExtended.newInstance(resolvedFilterOptions, v, groupBy))) : undefined;

                return handler.aggregateMarginCostValues(contractedCostValues, sourceCostValues, billingSource, retailCostValues);
            }

            return handler.groupResults(values[0].map(v => BillingAggregationResultExtended.newInstance(resolvedFilterOptions, v, groupBy)));
        }
        catch (e)
        {
            console.error("costAggregation failed", e);
            this.error.defaultError("Something went wrong, Please contact your administrator");
            return [];
        }
    }

    public async costAndUsageAggregation(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                         billingSource: string,
                                         groupBy: BillingGroupBy[],
                                         currency: Models.Currency = Models.Currency.USD,
                                         context: Models.DataContext,
                                         rangeExt: RangeSelectionExtended,
                                         options?: BillingAggregationOptions): Promise<BillingCostAndUsageAggregationResultExtended[]>
    {
        const {limit, domain, fetcher, platformAsKubernetes, platform, granularity} = options || {};
        const effectivePlatform                                                     = platformAsKubernetes ? (platform ?? Models.Platforms.kubernetes) : platform;

        try
        {
            const result = await this.costAndUsageTrend(filterOptions,
                                                         billingSource,
                                                         groupBy,
                                                         currency,
                                                         context,
                                                         rangeExt,
                                                         granularity ?? Models.TrendGranularity.day,
                                                         false,
                                                         limit,
                                                         undefined,
                                                         domain,
                                                         fetcher,
                                                         effectivePlatform);

            return result.values.map(v => BillingService.toUsageAggregation(v as BillingTrendResultExtended));
        }
        catch (e)
        {
            console.error("costAndUsageAggregation failed", e);
            this.error.defaultError("Something went wrong, Please contact your administrator");
            return [];
        }
    }

    private getK8ScopedCostFilteredContext(context: Models.DataContext): Models.DataContext
    {
        const filteredContext   = Models.DataContext.deepClone(context);
        inheritComponentId(context, filteredContext);
        const operatorsToRemove = [
            Models.UtilizationTypeQueryOperator.newInstance({}),
            Models.ContainerIdRegexQueryOperator.newInstance({}),
            Models.PlatFormAccountQueryOperator.newInstance({}),
            Models.PodIdRegexQueryOperator.newInstance({}),
            Models.PodNameRegexQueryOperator.newInstance({}),
            Models.ResourceTypeQueryOperator.newInstance({}),
            Models.ResourceQueryTagKeyValuesKind.newInstance({}),
            Models.ResourceQueryTagKeyKind.newInstance({}),
            Models.ResourceQueryExtraKeyValue.newInstance({})
        ];

        if (operatorsToRemove.length > 0)
        {
            filteredContext.query = WidgetQueryBuilderAssistant.removeOperators(filteredContext?.query, operatorsToRemove);
        }

        return filteredContext;
    }

    private async k8ScopedCostAggregation(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                          billingSource: string,
                                          groupBy: BillingGroupBy[],
                                          currency: Models.Currency,
                                          context: Models.DataContext,
                                          rangeExt: RangeSelectionExtended,
                                          options?: BillingAggregationOptions): Promise<BillingAggregationResult[]>
    {
        const filteredContext                           = this.getK8ScopedCostFilteredContext(context);
        const requestOptions: BillingAggregationOptions = {
            ...options,
            computeK8ScopedCost: false
        };
        const makeAggregation                           = (forKubernetes: boolean,
                                                           dataContext: Models.DataContext) =>
        {
            return this.costAggregationInternal(filterOptions,
                                                billingSource,
                                                groupBy,
                                                currency,
                                                dataContext,
                                                rangeExt,
                                                {
                                                    ...requestOptions,
                                                    platformAsKubernetes: forKubernetes
                                                },
                                                true);
        };
        const [k8Result, billingResult]                 = await Promise.all([
                                                                                makeAggregation(true, context),
                                                                                isAiPlatform(options?.platform) ? Promise.resolve(null) : makeAggregation(false, filteredContext)
                                                                            ]);
        const handler                                   = new BillingGroupByHandlerMulti(groupBy);
        return handler.computeK8AggregationValues(k8Result,
                                                  billingResult,
                                                  options?.fetcher,
                                                  groupBy,
                                                  options?.domain);
    }

    private processBillingGroups(groups: Models.BillingGroupByDetails[],
                                 groupBy: BillingGroupBy[],
                                 query: Models.QueryOperator,
                                 options?: {
                                     handleHierarchy?: boolean;
                                     trackResourceTagKeys?: boolean;
                                     trackKubernetesPod?: boolean;
                                 }): BillingGroupProcessingResult
    {
        const {
                  handleHierarchy,
                  trackResourceTagKeys,
                  trackKubernetesPod
              } = options || {};

        let hasTenantTags          = false;
        let hasServiceAccountTags  = false;
        let groupByResourceTagKeys = false;
        let groupByKubernetesPod   = false;

        for (const group of groups || [])
        {
            switch (group.selector)
            {
                case Models.BillingGroupBySelector.tags:
                    if (groupBy.some(g => g.selector === GroupBySelector.tag_keys))
                    {
                        if (trackResourceTagKeys) groupByResourceTagKeys = true;
                        group.selector = Models.BillingGroupBySelector.tags;
                        group.key      = null;
                    }
                    break;

                case Models.BillingGroupBySelector.extras:
                    if (groupBy.some(g => g.selector === GroupBySelector.pods))
                    {
                        if (trackKubernetesPod) groupByKubernetesPod = true;
                        group.selector = Models.BillingGroupBySelector.extras;
                        group.key      = Models.KubernetesWorkload.pod;
                    }
                    break;

                case Models.BillingGroupBySelector.hierarchy:
                    if (handleHierarchy) group.selector = Models.BillingGroupBySelector.hierarchy;
                    break;

                case Models.BillingGroupBySelector.tenant_tags:
                    hasTenantTags = true;
                    query         = WidgetQueryBuilderAssistant.merge(query, Models.TenantTagKeyQueryOperator.newInstance({tag_keys: [group.key]}));
                    break;

                case Models.BillingGroupBySelector.service_account_tags:
                    hasServiceAccountTags = true;
                    query                 = WidgetQueryBuilderAssistant.merge(query, Models.ServiceAccountTagKeyQueryOperator.newInstance({tag_keys: [group.key]}));
                    break;
            }
        }

        return {
            query,
            hasTenantTags,
            hasServiceAccountTags,
            groupByResourceTagKeys,
            groupByKubernetesPod
        };
    }

    private async processPlatformAccounts(query: Models.QueryOperator,
                                          platformAsKubernetes: boolean,
                                          domain: AppDomainContext,
                                          fetcher?: ServiceAccountFetcher,
                                          platform?: Models.Platforms): Promise<Models.QueryOperator>
    {
        let hasDescendantKubernetesServiceAccountScope = false;
        if (platformAsKubernetes && query)
        {
            hasDescendantKubernetesServiceAccountScope = !!findQueryOperatorByType(query, Models.DescendantServiceAccountQueryOperator);
        }

        if (!hasDescendantKubernetesServiceAccountScope)
        {
            let platform_account_ids: string[] = [];
            let service_account_ids: string[]  = [];
            let clusterType: string;

            if (platformAsKubernetes && query)
            {
                // Directly extract operators to avoid K8 assistant throwing on unregistered FinOps condition types.
                const serviceAccountOp = findQueryOperator<Models.ServiceAccountQueryOperator>(
                    query,
                    node => (<Models.ServiceAccountQueryOperator>node)?.service_account_ids != null
                );
                service_account_ids    = serviceAccountOp?.service_account_ids ?? [];

                const platformAccountOp = findQueryOperatorByType<Models.PlatFormAccountQueryOperator>(query, Models.PlatFormAccountQueryOperator);
                platform_account_ids    = platformAccountOp?.platform_account_ids ?? [];
                clusterType             = platformAccountOp?.cluster_type;

                // Only Kubernetes bills on cluster child accounts; AI spend sits on the selected service accounts themselves,
                // so deriving cluster accounts there would widen the query with unrelated accounts.
                if (service_account_ids.length && !platform_account_ids.length && !isAiPlatform(platform))
                {
                    const accountList    = await domain.accountGovernanceList.getClusterAccountOptions(platform ?? Models.Platforms.kubernetes, clusterType, service_account_ids, fetcher);
                    platform_account_ids = accountList.map(option => option.id);
                }

                if (platform_account_ids.length)
                {
                    // Remove ServiceAccountQueryOperator and add new one with platform_account_ids
                    query = WidgetQueryBuilderAssistant.remove(query, Models.ServiceAccountQueryOperator.newInstance({}));
                    query = WidgetQueryBuilderAssistant.merge(query, Models.ServiceAccountQueryOperator.newInstance({
                                                                                                                        service_account_ids: [
                                                                                                                            ...service_account_ids,
                                                                                                                            ...platform_account_ids
                                                                                                                        ]
                                                                                                                    }));
                }
            }

            query = WidgetQueryBuilderAssistant.remove(query, Models.PlatFormAccountQueryOperator.newInstance({}));
        }

        return query;
    }

    private async getStartEnd(context: Models.DataContext,
                              groupBy: BillingGroupBy[],
                              rangeExt: RangeSelectionExtended): Promise<[Date, Date]>
    {
        const operators    = findQueryOperators<Models.DimensionQueryOperator>(context.query, node => node instanceof Models.DimensionQueryOperator);
        const dimensionIds = operators.map(operator => operator.dimension_id);
        for (const gb of groupBy || [])
        {
            if (gb.selector == GroupBySelector.dimension)
            {
                const [key] = gb.key?.split("/") || [];
                if (key) dimensionIds.push(key);
            }
        }

        let intersection = rangeExt;
        const dimensions = await this.loadDimensionOptions();
        for (const dimensionId of dimensionIds)
        {
            const dimension = dimensions[dimensionId];
            if (dimension)
            {
                const dimensionRange    = Models.RangeSelection.newInstance({
                                                                                start: dimension.details.start_date,
                                                                                end  : dimension.details.end_date
                                                                            });
                const dimensionRangeExt = new RangeSelectionExtended(dimensionRange);
                intersection            = intersection ? intersection.intersection(dimensionRangeExt) : dimensionRangeExt;
                if (!intersection)
                {
                    const now = MomentHelper.now()
                                            .toDate();

                    // assign an empty range because there's no intersection between dimensions and provided rangeExt
                    return [
                        now,
                        now
                    ];
                }
            }
        }

        return intersection && [
            intersection.getMinAsDate(),
            intersection.getMaxAsDate()
        ];
    }

    public async trendData(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                           billingSource: string,
                           groupBy: BillingGroupBy[],
                           currency: Models.Currency,
                           context: Models.DataContext,
                           rangeExt: RangeSelectionExtended,
                           granularity: Models.TrendGranularity,
                           options?: BillingAggregationOptions): Promise<BillingTrendResult[]>
    {
        const {
                  limit,
                  forecastDuration,
                  computeForecast,
                  selectedAccountType,
                  lazyAccountTypeAggregation,
                  fetcher,
                  domain,
                  platformAsKubernetes,
                  platform,
                  actualDataStartTime,
                  actualDataEndTime,
                  includeRetailCost,
                  fiscalStartMonth
              } = options || {};

        if (!billingSource && WidgetCostMetricStandaloneCondition.isLoaded())
        {
            this.error.defaultError("You do not have access to any supported metrics required to display data on this dashboard. Please contact your administrator for access.");
        }
        const isBillingSourceMargin = this.isCostMetricMargin(billingSource);
        const groupByHandler        = new BillingGroupByHandlerMulti(groupBy);

        let result: BillingTrendDataInternal;
        if (isBillingSourceMargin)
        {
            result = await this.marginTrend(filterOptions, billingSource, groupBy,
                                            currency, context, rangeExt, granularity, limit,
                                            forecastDuration, computeForecast, platformAsKubernetes, platform,
                                            actualDataStartTime, actualDataEndTime, includeRetailCost, fiscalStartMonth,
                                            groupByHandler);
        }
        else if (platformAsKubernetes)
        {
            result = await this.k8Trend(filterOptions, billingSource, groupBy,
                                        currency, context, rangeExt, granularity, fetcher, limit,
                                        forecastDuration, computeForecast, domain, actualDataStartTime, actualDataEndTime,
                                        fiscalStartMonth, platform, groupByHandler);
        }
        else
        {
            result = await this.costTrend(filterOptions, billingSource, groupBy,
                                          currency, context, rangeExt, granularity, limit,
                                          forecastDuration, computeForecast, platformAsKubernetes, platform,
                                          actualDataStartTime, actualDataEndTime, fiscalStartMonth, groupByHandler);
        }

        const postProcessCtx = await this.prepareTrendPostProcess(filterOptions, billingSource, groupBy, currency, context, rangeExt, granularity, options, groupByHandler);

        let trends = this.applyTrendGrouping(postProcessCtx, result);

        if (lazyAccountTypeAggregation)
        {
            // Kept inline rather than folded into applyTrendPostProcess: the lazy grouping is derived
            // from the accounts present in the response, so it cannot be hoisted ahead of the rows.
            // That is also why the streaming path falls back for this case.
            // Exact type match: the duck-typed predicate below would also match
            // DescendantServiceAccountQueryOperator and strip the aggregated ancestor rows.
            const cloudAccountNode = findQueryOperatorByType<Models.ServiceAccountQueryOperator>(context.query, Models.ServiceAccountQueryOperator);
            const filteredValues   = await this.filterByAccountTypeLazily(trends, selectedAccountType, lazyAccountTypeAggregation, postProcessCtx.resolvedFilterOptions, groupBy, fetcher, billingSource);

            trends = cloudAccountNode?.service_account_ids?.length
                ? filteredValues.filter(trend => cloudAccountNode.service_account_ids.includes(trend.identity.model.service_account_id))
                : filteredValues;
        }
        else
        {
            trends = this.applyTrendAccountTypeFilter(postProcessCtx, trends);
        }

        return this.applyTrendAlignment(postProcessCtx, trends, result);
    }

    /**
     * Resolves everything the post-process tail needs that does not depend on the result rows, so the
     * tail itself can run synchronously and be re-run over a growing accumulator while streaming.
     */
    private async prepareTrendPostProcess(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                          billingSource: string,
                                          groupBy: BillingGroupBy[],
                                          currency: Models.Currency,
                                          context: Models.DataContext,
                                          rangeExt: RangeSelectionExtended,
                                          granularity: Models.TrendGranularity,
                                          options?: BillingAggregationOptions,
                                          groupByHandler?: BillingGroupByHandlerMulti): Promise<TrendPostProcessContext>
    {
        const {
                  forecastDuration,
                  selectedAccountType,
                  lazyAccountTypeAggregation,
                  fillEmptyTrendPoints,
                  platformAsKubernetes,
                  useActualValuesBeforeForecast,
                  forecastSplitTime: forecastSplitTimeOverride,
                  fiscalStartMonth
              } = options || {};

        const isT2Range = TimeRanges.resolve(rangeExt.model?.range).nowOffsetDays > 0;

        // Frozen once. Recomputed per flush, "now" drifts across the stream and moves the
        // actual/forecast split boundary between emissions.
        const forecastSplitTime     = forecastSplitTimeOverride ?? BillingService.forecastSplitTime(rangeExt.getMax()
                                                                                                            .valueOf(),
                                                                                                    MomentHelper.nowWithUTC()
                                                                                                                .valueOf(),
                                                                                                    isT2Range);
        const forecastTrimStartTime = isT2Range && granularity === Models.TrendGranularity.day && forecastDuration?.granularity === Models.TrendGranularity.day
            ? forecastSplitTime
            : undefined;

        const currencyConverter = context.organizationCurrency ? await this.currency.getCurrencyConverter(currency) : null;

        // Unconditional, matching costTrend: these feed BillingLineItemExtended, which resolves the
        // account and tenant behind every row. Resolving them only for the account-type branches
        // leaves identities unresolved on the streaming path, and the group-by keys derived from them
        // then bucket and label wrong — silently, since nothing throws on a null lookup.
        const resolvedFilterOptions = await this.resolveFilterOptions(filterOptions);

        let restrictToServiceAccountIds: string[] = null;
        if (!lazyAccountTypeAggregation && selectedAccountType)
        {
            const cloudAccountNode: Models.ServiceAccountQueryOperator = findQueryOperator(context.query,
                                                                                           (node) => (<Models.ServiceAccountQueryOperator>node)?.service_account_ids != null);

            restrictToServiceAccountIds = cloudAccountNode?.service_account_ids?.length ? cloudAccountNode.service_account_ids : null;
        }

        return {
            groupBy,
            granularity,
            billingSource,
            currency,
            context,
            rangeExt,
            isBillingSourceMargin: this.isCostMetricMargin(billingSource),
            platformAsKubernetes : !!platformAsKubernetes,
            fillEmptyTrendPoints : !!fillEmptyTrendPoints,
            hasForecastOrActuals : !!forecastDuration || !!useActualValuesBeforeForecast,
            rollUpToYearly       : granularity === Models.TrendGranularity.year && !!context.organizationCurrency,
            fiscalStartMonth     : fiscalStartMonth ?? 0,
            forecastSplitTime,
            forecastTrimStartTime,
            handler              : groupByHandler ?? new BillingGroupByHandlerMulti(groupBy),
            resolvedFilterOptions,
            currencyConverter,
            restrictToServiceAccountIds
        };
    }

    /**
     * The synchronous post-process tail. Re-running it over the whole accumulator on every flush is
     * what makes an intermediate emission definitionally the one-shot answer for the rows seen so far.
     */
    private applyTrendPostProcess(ctx: TrendPostProcessContext,
                                  data: BillingTrendDataInternal): BillingTrendResult[]
    {
        const grouped = this.applyTrendGrouping(ctx, data);

        return this.applyTrendAlignment(ctx, this.applyTrendAccountTypeFilter(ctx, grouped), data);
    }

    private applyTrendGrouping(ctx: TrendPostProcessContext,
                               data: BillingTrendDataInternal): BillingTrendResult[]
    {
        let processedValues = data.values;
        let processedActual = data.currGranularityActual;
        if (ctx.currencyConverter)
        {
            processedValues = data.values.map(v => v.convertCurrency(ctx.currencyConverter, ctx.currency));
            processedActual = data.currGranularityActual?.map(v => v.convertCurrency(ctx.currencyConverter, ctx.currency));
        }

        let trends = ctx.handler.groupTrend(processedValues,
                                            ctx.granularity,
                                            processedActual,
                                            ctx.isBillingSourceMargin,
                                            ctx.billingSource,
                                            ctx.platformAsKubernetes,
                                            ctx.hasForecastOrActuals,
                                            ctx.forecastSplitTime,
                                            ctx.fiscalStartMonth);

        if (ctx.rollUpToYearly)
        {
            trends = trends.map(v => v.convertToYearly());
        }

        return trends;
    }

    // Incident 140582: the non-lazy account-type path no longer aggregates over the service-account
    // subtree — the response already carries the aggregated rows — so this only narrows the result to
    // the explicitly selected accounts.
    private applyTrendAccountTypeFilter(ctx: TrendPostProcessContext,
                                        trends: BillingTrendResult[]): BillingTrendResult[]
    {
        return ctx.restrictToServiceAccountIds
            ? trends.filter(trend => ctx.restrictToServiceAccountIds.includes(trend.identity.model.service_account_id))
            : trends;
    }

    private applyTrendAlignment(ctx: TrendPostProcessContext,
                                trends: BillingTrendResult[],
                                data: BillingTrendDataInternal): BillingTrendResult[]
    {
        let rangeExt  = ctx.rangeExt;
        let startTime = data.startTime;
        let endTime   = data.endTime;
        let billingRange: RangeSelectionExtended;

        if (RangeSelectionExtended.isBillingRange(ctx.context.timeRange))
        {
            // Data-derived, so it legitimately grows with the accumulator and must stay per-flush.
            billingRange = this.getDateRangeFromTrendResult(data.values);
            rangeExt     = billingRange;
        }

        if (!ctx.fillEmptyTrendPoints) return trends;

        const granularityRanges = RangeSelectionExtended.splitRange(rangeExt, ctx.granularity);
        if (billingRange)
        {
            startTime = billingRange.getMinAsDate()
                                    .valueOf();
            endTime   = billingRange.getMaxAsDate()
                                    .valueOf();
        }

        return trends.map(trend => trend.alignTrendWithGranularityRanges(granularityRanges,
                                                                         startTime,
                                                                         endTime,
                                                                         data.forecastEndTime,
                                                                         ctx.billingSource,
                                                                         ctx.forecastTrimStartTime));
    }

    /** Runs the caller's own request as given — no currency conversion, platform-account rewriting or
     * forecast windowing — then reuses the existing grouping mechanism. Throws on failure. */
    public async getGroupedCostTrend(request: Models.BillingAggregationTrendRequest,
                                     groupBy: BillingGroupBy[],
                                     filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                     componentId?: string): Promise<BillingTrendResult[]>
    {
        const valuesRaw             = await withComponentId(componentId, () => this.api.billing.getCostAggregationTrend(request));
        const resolvedFilterOptions = await this.resolveFilterOptions(filterOptions);

        const wrapped = (valuesRaw || []).map((v) => BillingTrendResultExtended.newInstance(resolvedFilterOptions, v, groupBy, false));

        const handler = new BillingGroupByHandlerMulti(groupBy);
        return handler.groupTrend(wrapped, Models.TrendGranularity.day, [], false, request.billing_source, false);
    }

    protected async marginTrend(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                billingSource: string,
                                groupBy: BillingGroupBy[],
                                currency: Models.Currency,
                                context: Models.DataContext,
                                rangeExt: RangeSelectionExtended,
                                granularity: Models.TrendGranularity,
                                limit?: number,
                                forecastDuration?: ForecastDuration, //when adding some preselected duration extra
                                computeForecast?: boolean, /* whether to compute forecast when rangeExt goes into future */
                                platformAsKubernetes?: boolean,
                                platform?: Models.Platforms,
                                actualDataStartTime?: number,
                                actualDataEndTime?: number,
                                includeRetailCost?: boolean,
                                fiscalStartMonth?: number,
                                groupByHandler?: BillingGroupByHandlerMulti): Promise<BillingTrendDataInternal>
    {
        const [contractedSource, actualSource] = this.getBillingSources(billingSource);
        const handler                          = groupByHandler ?? new BillingGroupByHandlerMulti(groupBy);

        // contractedCost/actualCost/retailCost differ only in billing_source: the query, forecast window,
        // and platform-account resolution (which can itself be a network call) are identical across all
        // three, so the plan is derived once here and stamped per source instead of being rebuilt from
        // scratch inside each parallel costTrend leg.
        const basePlan = this.buildCostTrendPlanBase(groupBy, currency, context, rangeExt, granularity,
                                                     limit, forecastDuration, computeForecast, platformAsKubernetes,
                                                     platform, actualDataStartTime, actualDataEndTime, fiscalStartMonth,
                                                     handler);
        const planFor  = (source: string): Promise<CostTrendPlan> => basePlan.then(plan => BillingService.stampBillingSource(plan, source));

        const retailCostPromise: Promise<BillingTrendDataInternal | null> = includeRetailCost
            ? this.fetchCostTrend(filterOptions, groupBy, context, rangeExt, planFor("raw_list_cost"))
            : Promise.resolve(null);

        const [contractedCost, actualCost, retailCost] = await Promise.all([
                                                                   this.fetchCostTrend(filterOptions, groupBy, context, rangeExt, planFor(contractedSource)),
                                                                   this.fetchCostTrend(filterOptions, groupBy, context, rangeExt, planFor(actualSource)),
                                                                   retailCostPromise
                                                               ]);
        const updatedCurrGranularity       = handler.computeMarginTrendValues(contractedCost.currGranularityActual, actualCost.currGranularityActual, billingSource, retailCost?.currGranularityActual);
        const margin                       = handler.computeMarginTrendValues(contractedCost.values, actualCost.values, billingSource, retailCost?.values);

        return {
            values               : margin,
            currGranularityActual: updatedCurrGranularity,
            startTime            : contractedCost.startTime,
            endTime              : contractedCost.endTime,
            forecastEndTime      : contractedCost.forecastEndTime
        };
    }

    protected async k8Trend(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                            billingSource: string,
                            groupBy: BillingGroupBy[],
                            currency: Models.Currency,
                            context: Models.DataContext,
                            rangeExt: RangeSelectionExtended,
                            granularity: Models.TrendGranularity,
                            fetcher: ServiceAccountFetcher,
                            limit?: number,
                            forecastDuration?: ForecastDuration, //when adding some preselected duration extra
                            computeForecast?: boolean, /* whether to compute forecast when rangeExt goes into future */
                            domain?: AppDomainContext,
                            actualDataStartTime?: number,
                            actualDataEndTime?: number,
                            fiscalStartMonth?: number,
                            platform?: Models.Platforms,
                            groupByHandler?: BillingGroupByHandlerMulti): Promise<BillingTrendDataInternal>
    {
        const filteredContext = this.getK8ScopedCostFilteredContext(context);
        const handler         = groupByHandler ?? new BillingGroupByHandlerMulti(groupBy);

        const makeCostTrendRequest = (forKubernetes: boolean) =>
        {
            return this.costTrend(filterOptions,
                                  billingSource,
                                  groupBy,
                                  currency,
                                  forKubernetes ? context : filteredContext,
                                  rangeExt,
                                  granularity,
                                  limit,
                                  forecastDuration,
                                  computeForecast,
                                  forKubernetes,
                                  platform,
                                  actualDataStartTime,
                                  actualDataEndTime,
                                  fiscalStartMonth,
                                  handler);
        };
        const [
                  k8Result,
                  billingResult
              ]                    = await Promise.all([
                                                           makeCostTrendRequest(true),
                                                           isAiPlatform(platform) ? Promise.resolve(null) : makeCostTrendRequest(false)
                                                       ]);

        const values                = await handler.computeK8TrendValues(k8Result.values, billingResult?.values, fetcher, groupBy, domain);
        const currGranularityActual = await handler.computeK8TrendValues(k8Result.currGranularityActual, billingResult?.currGranularityActual, fetcher, groupBy, domain);

        return {
            values               : values,
            currGranularityActual: currGranularityActual,
            startTime            : k8Result.startTime,
            endTime              : k8Result.endTime,
            forecastEndTime      : k8Result.forecastEndTime
        };
    }

    private async costTrend(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                            billingSource: string,
                            groupBy: BillingGroupBy[],
                            currency: Models.Currency,
                            context: Models.DataContext,
                            rangeExt: RangeSelectionExtended,
                            granularity: Models.TrendGranularity,
                            limit: number = 0,
                            forecastDuration?: ForecastDuration, //when adding some preselected duration extra
                            computeForecast?: boolean,
                            platformAsKubernetes?: boolean,
                            platform?: Models.Platforms,
                            actualDataStartTime?: number,
                            actualDataEndTime?: number,
                            fiscalStartMonth: number = 0,
                            groupByHandler?: BillingGroupByHandlerMulti): Promise<BillingTrendDataInternal>
    {
        const plan = this.buildCostTrendPlan(billingSource, groupBy, currency, context, rangeExt, granularity,
                                             limit, forecastDuration, computeForecast, platformAsKubernetes,
                                             platform, actualDataStartTime, actualDataEndTime, fiscalStartMonth,
                                             groupByHandler);

        return this.fetchCostTrend(filterOptions, groupBy, context, rangeExt, plan);
    }

    private async fetchCostTrend(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                 groupBy: BillingGroupBy[],
                                 context: Models.DataContext,
                                 rangeExt: RangeSelectionExtended,
                                 planPromise: Promise<CostTrendPlan>): Promise<BillingTrendDataInternal>
    {
        try
        {
            const plan = await planPromise;

            if (plan.degenerate)
            {
                return {
                    values               : [],
                    currGranularityActual: [],
                    startTime            : plan.startTime,
                    endTime              : plan.endTime,
                    forecastEndTime      : plan.forecastEndTime
                };
            }

            const componentId              = componentIdOf(context);
            const valuesRaw                = await withComponentId(componentId, () => this.api.billing.getCostAggregationTrend(plan.request));
            const currGranularityActualRaw = plan.notForecastedRequest
                                             && await withComponentId(componentId, () => this.api.billing.getCostAggregationTrend(plan.notForecastedRequest));

            const resolvedFilterOptions = await this.resolveFilterOptions(filterOptions);

            return {
                values               : this.materializeTrendRows(valuesRaw, resolvedFilterOptions, groupBy, plan.isBillingRangeMonth),
                currGranularityActual: currGranularityActualRaw ? this.materializeTrendRows(currGranularityActualRaw, resolvedFilterOptions, groupBy, plan.isBillingRangeMonth) : undefined,
                startTime            : plan.startTime,
                endTime              : plan.endTime,
                forecastEndTime      : plan.forecastEndTime
            };
        }
        catch (e)
        {
            this.trendApiStatus("failed");
            return {
                values               : [],
                currGranularityActual: [],
                startTime            : rangeExt.getMin()
                                               .valueOf(),
                endTime              : rangeExt.getMax()
                                               .valueOf(),
                forecastEndTime      : undefined
            };
        }
    }

    private materializeTrendRows(rows: Models.BillingAggregationTrendResult[],
                                 resolvedFilterOptions: FilterOptionsExtended,
                                 groupBy: BillingGroupBy[],
                                 isBillingRangeMonth: boolean): BillingTrendResultExtended[]
    {
        return rows.map(v => BillingTrendResultExtended.newInstance(resolvedFilterOptions, v, groupBy, isBillingRangeMonth));
    }

    /**
     * Thin per-source wrapper around `buildCostTrendPlanBase`. Kept as its own method since most callers
     * (the one-shot single-source path and the streaming path) have exactly one billing source and no
     * reason to separate plan-building from stamping it.
     */
    private async buildCostTrendPlan(billingSource: string,
                                     groupBy: BillingGroupBy[],
                                     currency: Models.Currency,
                                     context: Models.DataContext,
                                     rangeExt: RangeSelectionExtended,
                                     granularity: Models.TrendGranularity,
                                     limit: number = 0,
                                     forecastDuration?: ForecastDuration,
                                     computeForecast?: boolean,
                                     platformAsKubernetes?: boolean,
                                     platform?: Models.Platforms,
                                     actualDataStartTime?: number,
                                     actualDataEndTime?: number,
                                     fiscalStartMonth: number = 0,
                                     groupByHandler?: BillingGroupByHandlerMulti): Promise<CostTrendPlan>
    {
        const basePlan = await this.buildCostTrendPlanBase(groupBy, currency, context, rangeExt, granularity,
                                                            limit, forecastDuration, computeForecast, platformAsKubernetes,
                                                            platform, actualDataStartTime, actualDataEndTime, fiscalStartMonth,
                                                            groupByHandler);

        return BillingService.stampBillingSource(basePlan, billingSource);
    }

    private static stampBillingSource(basePlan: CostTrendPlan,
                                      billingSource: string): CostTrendPlan
    {
        if (basePlan.degenerate) return basePlan;

        return {
            ...basePlan,
            request             : Models.BillingAggregationTrendRequest.newInstance({...basePlan.request, billing_source: billingSource}),
            notForecastedRequest: basePlan.notForecastedRequest
                ? Models.BillingAggregationTrendRequest.newInstance({...basePlan.notForecastedRequest, billing_source: billingSource})
                : basePlan.notForecastedRequest
        };
    }

    /**
     * Everything `costTrend` does before the fetch, minus the billing source (see `stampBillingSource`).
     * Split out so the streaming path can build the same request and issue it before awaiting anything
     * that would delay the network call.
     *
     * Takes no filterOptions on purpose — resolving them here would defeat the choke point that lets
     * callers forward an unresolved promise.
     */
    private async buildCostTrendPlanBase(groupBy: BillingGroupBy[],
                                         currency: Models.Currency,
                                         context: Models.DataContext,
                                         rangeExt: RangeSelectionExtended,
                                         granularity: Models.TrendGranularity,
                                         limit: number = 0,
                                         forecastDuration?: ForecastDuration,
                                         computeForecast?: boolean,
                                         platformAsKubernetes?: boolean,
                                         platform?: Models.Platforms,
                                         actualDataStartTime?: number,
                                         actualDataEndTime?: number,
                                         fiscalStartMonth: number = 0,
                                         groupByHandler?: BillingGroupByHandlerMulti): Promise<CostTrendPlan>
    {
        const organizationCurrency = context.organizationCurrency;
        let query                  = context.query;

        const handler = groupByHandler ?? new BillingGroupByHandlerMulti(groupBy);

        const combinedGroupBy = handler.combinedGroupBy(!organizationCurrency);
        const groups          = combinedGroupBy.group_by;

        if (groups.length === 1)
        {
            const group = groups[0];
            if (group.selector == Models.BillingGroupBySelector.dimension && !group.key && !group.groups?.length)
            {
                return {
                    request             : null,
                    notForecastedRequest: null,
                    isBillingRangeMonth : false,
                    startTime           : rangeExt.getMin()
                                                  .valueOf(),
                    endTime             : rangeExt.getMax()
                                                  .valueOf(),
                    forecastEndTime     : undefined,
                    degenerate          : true
                };
            }
        }

        const groupProcessing = this.processBillingGroups(groups, groupBy, query, {
            handleHierarchy     : true,
            trackResourceTagKeys: true,
            trackKubernetesPod  : true
        });
        query                 = groupProcessing.query;

        query = this.ensureNotOnboardedStatusForCspCustomers(query);
        query = WidgetQueryBuilderAssistant.adjustTagKeyQuery(groups, groupBy, query, groupProcessing.hasServiceAccountTags, groupProcessing.hasTenantTags);

        // To handle currency conversion we have to request monthly data so we can do proper conversion
        const requestGranularity = organizationCurrency && granularity == Models.TrendGranularity.year ? Models.TrendGranularity.month : granularity;

        const timeRangeId     = TimeRanges.resolve(context?.timeRange?.range).id;
        const isBillingRange  = TimeRange.isBillingTimeRange(timeRangeId);
        const nowOffsetDays   = TimeRanges.resolve(rangeExt.model?.range).nowOffsetDays;
        const isT2Range       = nowOffsetDays > 0;

        query         = await this.processPlatformAccounts(query, platformAsKubernetes, this.currency.domain, undefined, platform);
        query         = this.appendCurrencyFilter(query, organizationCurrency ? null : currency);
        const request = Models.BillingAggregationTrendRequest.newInstance({
                                                                              billing_mode: isBillingRange,
                                                                              ...combinedGroupBy,
                                                                              filters    : query,
                                                                              start_date : rangeExt.getMinAsDate(),
                                                                              end_date   : rangeExt.model.range
                                                                                  ? BillingService.alignToHourBoundary(rangeExt.getMaxAsDate())
                                                                                  : rangeExt.getMaxAsDate(),
                                                                              granularity: granularitySeconds(requestGranularity),
                                                                              ...(platformAsKubernetes && {platform: platform ?? Models.Platforms.kubernetes})
                                                                          });
        if (limit)
        {
            request.limit = limit;
        }

        let endTime: number = request.end_date.valueOf();
        let forecastEndTime: number;

        let notForecastedRequest: Models.BillingAggregationTrendRequest;
        if (forecastDuration || computeForecast)
        {
            const now = new Date();
            rangeExt  = new RangeSelectionExtended(rangeExt.model);
            let min   = rangeExt.getMin()
                                .clone();
            let max   = rangeExt.getMax()
                                .clone();
            const selectedRangeEnd = max.valueOf();

            let forecastValid = !forecastDuration;
            if (forecastDuration)
            {
                // only request for a forecast if max goes up to now
                const allowedDelta = 5;
                if (!forecastValid)
                {
                    // allowing for up to a window of allowedDelta seconds before now for an abundance of caution
                    max.add(allowedDelta, "second");
                    // T-2 presets end nowOffsetDays before "now" by design, so treat them as reaching the present to still forecast (0 for all other presets — no-op).
                    forecastValid = max.toDate() > now || isT2Range;
                }
            }

            if (forecastValid)
            {
                const notForecastedStart = MomentHelper.min(min,
                                                            MomentHelper.parseAsUTC(normalizeForecastPeriodStart(now,
                                                                                                                 requestGranularity,
                                                                                                                 fiscalStartMonth)));
                notForecastedRequest     = Models.BillingAggregationTrendRequest.newInstance({
                                                                                                 ...request,
                                                                                                 start_date: notForecastedStart.toDate(),
                                                                                                 end_date  : now
                                                                                             });
                if (actualDataStartTime != null)
                {
                    notForecastedRequest.start_date = new Date(actualDataStartTime);
                }
                request.compute_forecast = true;

                if (forecastDuration)
                {
                    request.start_date            = min.toDate();
                    request.end_date              = new Date(forecastRangeEnd(selectedRangeEnd,
                                                                              now.valueOf(),
                                                                              forecastDuration,
                                                                              granularity,
                                                                              fiscalStartMonth,
                                                                              nowOffsetDays));
                    forecastEndTime               = request.end_date.valueOf();
                    notForecastedRequest.end_date = new Date(BillingService.actualRequestEndTime(selectedRangeEnd,
                                                                                                 now.valueOf(),
                                                                                                 isT2Range));
                }
                else
                {
                    // forecasting through custom range
                    forecastEndTime = endTime;
                }

                if (actualDataEndTime != null)
                {
                    notForecastedRequest.end_date = new Date(actualDataEndTime);
                }

                endTime = BillingService.forecastActualEndTime(selectedRangeEnd, now.valueOf(), isT2Range);
            }
        }

        const computedRangeExt = new RangeSelectionExtended(Models.RangeSelection.newInstance({
                                                                                                  start: MomentHelper.toLocalDate(MomentHelper.parseAsUTC(request.start_date)),
                                                                                                  end  : MomentHelper.toLocalDate(MomentHelper.parseAsUTC(request.end_date))
                                                                                              }));
        const startEnd         = await this.getStartEnd(context, groupBy, computedRangeExt);
        if (startEnd)
        {
            request.end_date = startEnd[1];
            if (forecastEndTime)
            {
                if (Math.min(forecastEndTime, request.end_date.valueOf()) <= endTime)
                {
                    request.compute_forecast = false;
                    notForecastedRequest     = null;
                }
                else
                {
                    request.end_date = new Date(forecastEndTime);
                }
            }

            const dimStart = startEnd[0].valueOf();
            if (dimStart > request.start_date.valueOf())
            {
                request.start_date = new Date(dimStart);
                if (dimStart > notForecastedRequest?.start_date.valueOf()) notForecastedRequest.start_date = request.start_date;
            }
        }

        return {
            request,
            notForecastedRequest,
            isBillingRangeMonth: isBillingRange && granularity === Models.TrendGranularity.month,
            startTime          : rangeExt.getMin()
                                         .valueOf(),
            endTime,
            forecastEndTime,
            degenerate         : false
        };
    }

    public trendApiStatus(apiStatus: string): string
    {
        this.costTrendApiStatus = apiStatus;
        return this.costTrendApiStatus;
    }

    private async costAndUsageTrend(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                    billingSource: string,
                                    groupBy: BillingGroupBy[],
                                    currency: Models.Currency,
                                    context: Models.DataContext,
                                    rangeExt: RangeSelectionExtended,
                                    granularity: Models.TrendGranularity,
                                    includeChargeDescription: boolean,
                                    limit: number,
                                    isActualSource?: boolean,
                                    domain?: AppDomainContext,
                                    fetcher?: ServiceAccountFetcher,
                                    platform?: Models.Platforms,
                                    groupByHandler?: BillingGroupByHandlerMulti): Promise<BillingTrendDataInternal>
    {
        const basePlan = this.buildCostAndUsageTrendPlanBase(groupBy, currency, context, rangeExt, granularity,
                                                              includeChargeDescription, limit, domain, fetcher, platform, groupByHandler);
        const plan     = basePlan.then(base => BillingService.stampCostAndUsageBillingSource(base, billingSource));

        return this.fetchCostAndUsageTrend(filterOptions, groupBy, context, isActualSource, plan);
    }

    /**
     * Fetch + materialize tail of `costAndUsageTrend`, taking the plan as a promise so `marginRate` can
     * build it once and stamp it per source instead of passing a freshly-derived plan into each leg.
     */
    private async fetchCostAndUsageTrend(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                         groupBy: BillingGroupBy[],
                                         context: Models.DataContext,
                                         isActualSource: boolean | undefined,
                                         planPromise: Promise<CostAndUsageTrendPlan>): Promise<BillingTrendDataInternal>
    {
        const plan      = await planPromise;
        const valuesRaw = await withComponentId(componentIdOf(context), () => this.api.billing.costAndUsageAggregationTrend(plan.request));

        const resolvedFilterOptions = await this.resolveFilterOptions(filterOptions);
        const values                = valuesRaw.map(v =>
                                                        BillingTrendResultExtended.newInstance(resolvedFilterOptions, v, groupBy, plan.isBillingRangeMonth, true, isActualSource)
        );

        return {
            values,
            startTime: plan.startTime,
            endTime  : plan.endTime
        };
    }

    /**
     * A plan's billing source affects nothing but the `billing_source` field on its request — every
     * other input is billing-source-independent. `marginRate` relies on this to build the plan once and
     * stamp it per source instead of re-deriving an identical plan on each of its (up to three) parallel legs.
     */
    private static stampCostAndUsageBillingSource(basePlan: CostAndUsageTrendPlan,
                                                  billingSource: string): CostAndUsageTrendPlan
    {
        return {
            ...basePlan,
            request: Models.BillingCostAndUsageAggregationRequest.newInstance({...basePlan.request, billing_source: billingSource})
        };
    }

    /**
     * Everything `costAndUsageTrend` does before the fetch, minus the billing source (see
     * `stampCostAndUsageBillingSource`).
     */
    private async buildCostAndUsageTrendPlanBase(groupBy: BillingGroupBy[],
                                                 currency: Models.Currency,
                                                 context: Models.DataContext,
                                                 rangeExt: RangeSelectionExtended,
                                                 granularity: Models.TrendGranularity,
                                                 includeChargeDescription: boolean,
                                                 limit: number,
                                                 domain?: AppDomainContext,
                                                 fetcher?: ServiceAccountFetcher,
                                                 platform?: Models.Platforms,
                                                 groupByHandler?: BillingGroupByHandlerMulti): Promise<CostAndUsageTrendPlan>
    {
        const organizationCurrency = context.organizationCurrency;
        let query                  = context.query;

        const handler = groupByHandler ?? new BillingGroupByHandlerMulti(groupBy);

        const combinedGroupBy = handler.combinedGroupBy();
        const groups          = combinedGroupBy.group_by;
        const groupProcessing = this.processBillingGroups(groups, groupBy, query, {
            trackResourceTagKeys: true,
            trackKubernetesPod  : true
        });
        query                 = groupProcessing.query;

        query = this.ensureNotOnboardedStatusForCspCustomers(query);
        query = WidgetQueryBuilderAssistant.adjustTagKeyQuery(groups, groupBy, query, groupProcessing.hasServiceAccountTags, groupProcessing.hasTenantTags);
        query = await this.processPlatformAccounts(query, !!platform, domain, fetcher, platform);
        query = this.appendCurrencyFilter(query, organizationCurrency ? null : currency);

        const requestGranularity = organizationCurrency && granularity === Models.TrendGranularity.year
            ? Models.TrendGranularity.month
            : granularity;
        const request            = Models.BillingCostAndUsageAggregationRequest.newInstance({
                                                                                                ...combinedGroupBy,
                                                                                                filters                   : query,
                                                                                                start_date                : rangeExt.getMinAsDate(),
                                                                                                end_date                  : rangeExt.getMaxAsDate(),
                                                                                                granularity               : granularitySeconds(requestGranularity),
                                                                                                include_charge_description: includeChargeDescription,
                                                                                                limit,
                                                                                                ...(platform ? {platform} : {})
                                                                                            });

        const isBillingRange      = TimeRange.isBillingTimeRange(TimeRanges.resolve(context.timeRange.range).id);
        const isBillingRangeMonth = isBillingRange && granularity === Models.TrendGranularity.month;

        return {
            request,
            isBillingRangeMonth,
            startTime: rangeExt.getMin()
                               .valueOf(),
            endTime  : request.end_date.valueOf()
        };
    }

    public async trendRate(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                           billingSource: string,
                           groupBy: BillingGroupBy[],
                           currency: Models.Currency,
                           context: Models.DataContext,
                           rangeExt: RangeSelectionExtended,
                           granularity: Models.TrendGranularity,
                           includeChargeDescription: boolean,
                           limit?: number,
                           domain?: AppDomainContext,
                           fetcher?: ServiceAccountFetcher,
                           platform?: Models.Platforms,
                           includeRetailCost?: boolean,
                           fromDashboard: boolean = false,
                           fillEmptyTrendPoints: boolean = false): Promise<BillingTrendResult[]>
    {
        if (!billingSource && WidgetCostMetricStandaloneCondition.isLoaded())
        {
            this.error.defaultError(
                "You do not have access to the required billing metrics. Please contact your administrator to request access."
            );
        }

        if (granularity === Models.TrendGranularity.day && rangeExt.getMax()
                                                                     .diff(rangeExt.getMin(), "days") > 31)
        {
            this.error.warn(
                "The date range cannot exceed 31 days for Day granularity when Usage or Rate is selected in the widget.",
                -1
            );
            return [];
        }

        const isBillingSourceMargin = this.isCostMetricMargin(billingSource);

        let result: BillingTrendDataInternal;
        if (isBillingSourceMargin)
        {
            result = await this.marginRate(filterOptions, billingSource, groupBy,
                                           currency, context, rangeExt, granularity, includeChargeDescription,
                                           limit, domain, fetcher, includeRetailCost, platform);
        }
        else
        {
            result = await this.costAndUsageTrend(filterOptions,
                                                  billingSource,
                                                  groupBy,
                                                  currency,
                                                  context,
                                                  rangeExt,
                                                  granularity,
                                                  includeChargeDescription,
                                                  limit,
                                                  undefined,
                                                  domain,
                                                  fetcher,
                                                  platform);
        }

        let values = result.values;

        if (fillEmptyTrendPoints)
        {
            const granularityRanges = RangeSelectionExtended.splitRange(rangeExt, granularity);
            values                  = values.map(trend => trend.alignTrendWithGranularityRanges(
                granularityRanges,
                result.startTime,
                result.endTime,
                undefined,
                billingSource
            ));
        }

        if (fromDashboard && !this.isProfitMargin(billingSource))
        {
            return this.collapseTrendForDashboard(values, billingSource, groupBy);
        }

        return values;
    }

    private collapseTrendForDashboard(values: BillingTrendResult[],
                                      billingSource: string,
                                      groupBy: BillingGroupBy[]): BillingTrendResult[]
    {
        const handler = new BillingGroupByHandlerMulti(groupBy);
        const groups  = new Map<string, BillingTrendResult[]>();
        for (const result of values)
        {
            const key   = handler.getKey(result.identity);
            const group = groups.get(key);
            if (group) group.push(result);
            else groups.set(key, [result]);
        }

        return Array.from(groups.values()).map(group => this.mergeGroupedResults(group, billingSource));
    }

    private mergeGroupedResults(group: BillingTrendResult[],
                                billingSource: string): BillingTrendResult
    {
        const identity    = group[0].identity;
        const numAccounts = group.reduce((sum, result) => sum + (result.numAccounts ?? 0), 0);

        if (group[0] instanceof BillingMarginResultExtended)
        {
            const margins   = group as BillingMarginResultExtended[];
            const hasRetail = margins.some(m => m.retailCost);
            return new BillingMarginResultExtended(identity,
                                                   this.mergePointsByTimestamp(margins.map(m => m.sourceCost)),
                                                   this.mergePointsByTimestamp(margins.map(m => m.contractedCost)),
                                                   this.mergePointsByTimestamp(margins.map(m => m.margin)),
                                                   this.mergePointsByTimestamp(margins.map(m => m.profitMargin)),
                                                   numAccounts,
                                                   billingSource,
                                                   margins[0].forecastedSource,
                                                   margins[0].forecastedContracted,
                                                   margins[0].forecastedMargin,
                                                   margins[0].forecastedProfitMargin,
                                                   hasRetail ? this.mergePointsByTimestamp(margins.map(m => m.retailCost)) : undefined);
        }

        return new BillingTrendResultExtended(identity,
                                              this.mergePointsByTimestamp(group.map(result => result.trend)),
                                              group[0].forecastedTrend,
                                              numAccounts);
    }

    private mergePointsByTimestamp(sources: BillingTrendPoints[]): BillingTrendPoints
    {
        const costByTs      = new Map<number, number>();
        const usageByTs     = new Map<number, number>();
        const rateSumByTs   = new Map<number, number>();
        const rateCountByTs = new Map<number, number>();
        const meta          = new Map<number, {timestamp: Date; billingTimestamp: Date}>();

        for (const source of sources)
        {
            for (const point of source?.points ?? [])
            {
                const ts = point.timestamp.valueOf();
                if (!meta.has(ts)) meta.set(ts, {timestamp: point.timestamp, billingTimestamp: point.billingTimestamp});

                costByTs.set(ts, (costByTs.get(ts) ?? 0) + point.value);

                if (point.usage != null) usageByTs.set(ts, (usageByTs.get(ts) ?? 0) + point.usage);

                if (point.rate != null)
                {
                    rateSumByTs.set(ts, (rateSumByTs.get(ts) ?? 0) + point.rate);
                    rateCountByTs.set(ts, (rateCountByTs.get(ts) ?? 0) + 1);
                }
            }
        }

        const points = Array.from(meta.keys())
                            .sort((a, b) => a - b)
                            .map(ts =>
                                 {
                                     const {timestamp, billingTimestamp} = meta.get(ts)!;
                                     const rateCount                      = rateCountByTs.get(ts) ?? 0;
                                     const rate                           = rateCount > 0 ? rateSumByTs.get(ts)! / rateCount : undefined;
                                     const usage                          = usageByTs.has(ts) ? usageByTs.get(ts)! : undefined;
                                     return new BillingTrendPoint(costByTs.get(ts) ?? 0, timestamp, billingTimestamp, undefined, rate, usage);
                                 });

        return new BillingTrendPoints(points);
    }

    private async marginRate(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                             billingSource: string,
                             groupBy: BillingGroupBy[],
                             currency: Models.Currency,
                             context: Models.DataContext,
                             rangeExt: RangeSelectionExtended,
                             granularity: Models.TrendGranularity,
                             includeChargeDescription: boolean,
                             limit: number,
                             domain?: AppDomainContext,
                             fetcher?: ServiceAccountFetcher,
                             includeRetailCost?: boolean,
                             platform?: Models.Platforms): Promise<BillingTrendDataInternal>
    {
        const [contractedSource, actualSource] = this.getBillingSources(billingSource);
        const handler                          = new BillingGroupByHandlerMulti(groupBy);

        // contractedCost/actualCost/retailCost differ only in billing_source (and isActualSource, which
        // only affects how the response rows are tagged, not the request): the query and platform-account
        // resolution are identical across all three, so the plan is derived once and stamped per source.
        const basePlan = this.buildCostAndUsageTrendPlanBase(groupBy, currency, context, rangeExt, granularity,
                                                              includeChargeDescription, limit, domain, fetcher, platform, handler);
        const planFor  = (source: string): Promise<CostAndUsageTrendPlan> => basePlan.then(plan => BillingService.stampCostAndUsageBillingSource(plan, source));

        const [contractedCost, actualCost, retailCost] = await Promise.all([
                                                                       this.fetchCostAndUsageTrend(filterOptions, groupBy, context, undefined, planFor(contractedSource)),
                                                                       this.fetchCostAndUsageTrend(filterOptions, groupBy, context, true, planFor(actualSource)),
                                                                       includeRetailCost
                                                                           ? this.fetchCostAndUsageTrend(filterOptions, groupBy, context, undefined, planFor("raw_list_cost"))
                                                                           : Promise.resolve(null)
                                                                   ]);

        const margin = handler.computeMarginTrendValues(contractedCost.values, actualCost.values, billingSource, retailCost?.values);

        return {
            values   : margin,
            startTime: contractedCost.startTime,
            endTime  : contractedCost.endTime
        };
    }

    // Restore the currency scoping that the backend previously inferred from service_account_ids.
    private appendCurrencyFilter(query: Models.QueryOperator,
                                 currency: Models.Currency): Models.QueryOperator
    {
        if (!currency) return query;

        return QueryOperatorLogicNode.andAppendQuery(query,
                                                     Models.CurrencyQueryOperator.newInstance({currencies: [currency]}));
    }

    private ensureNotOnboardedStatusForCspCustomers(query: Models.QueryOperator): Models.QueryOperator
    {
        // Exact type match: the duck-typed service_account_ids predicate would also match a
        // DescendantServiceAccountQueryOperator (lazy scope), which this CSP fixup must not touch.
        const serviceAccountIdList = findQueryOperatorByType<Models.ServiceAccountQueryOperator>(query,
                                                                                                 Models.ServiceAccountQueryOperator)?.service_account_ids
                                     || [];

        if (!serviceAccountIdList.length) return query;

        const selectedStatuses = findQueryOperator<Models.ServiceAccountStatusQueryOperator>(query, node => (<Models.ServiceAccountStatusQueryOperator>node)?.statuses != null)?.statuses || [];

        if (selectedStatuses.includes(Models.ServiceAccountStatus.not_onboarded)) return query;

        const nativeDetailsArray = serviceAccountIdList.map(id => ServiceAccountService.accountNativeDetailsMap.get(id))
                                                       .filter(Boolean);
        const hasCspCustomer     = nativeDetailsArray.some(item => item instanceof Models.ServiceAccountNativeDetailsForAzureCspCustomer);

        if (selectedStatuses.length > 0 && hasCspCustomer)
        {
            return WidgetQueryBuilderAssistant.merge(query, Models.ServiceAccountStatusQueryOperator.newInstance({
                                                                                                                     statuses: [
                                                                                                                         ...selectedStatuses,
                                                                                                                         Models.ServiceAccountStatus.not_onboarded
                                                                                                                     ]
                                                                                                                 }));
        }

        return query;
    }

    public async descriptionTrend(filterOptions: FilterOptionsExtended | Promise<FilterOptionsExtended>,
                                  billingSource: string,
                                  groupBy: BillingGroupBy[],
                                  currency: Models.Currency,
                                  context: Models.DataContext,
                                  rangeExt: RangeSelectionExtended,
                                  granularity: Models.TrendGranularity,
                                  momentHelperKeepLocalTime: boolean = false, // todo: remove. This should be false and we should not have an option for it to be true
                                  limit: number                      = 0,
                                  isMultiCloudSaas?: boolean,
                                  includeRetailCost?: boolean): Promise<BillingTrendResult[]>
    {
        let resolvedFilterOptions: FilterOptionsExtended;
        const isBillingSourceMargin = this.isCostMetricMargin(billingSource);
        if (isBillingSourceMargin)
        {
            resolvedFilterOptions ||= await Promise.resolve(filterOptions);
            return this.marginDescriptionTrend(resolvedFilterOptions, billingSource, groupBy, currency, context, rangeExt, granularity, momentHelperKeepLocalTime, limit, includeRetailCost);
        }

        const handler         = new BillingGroupByHandlerMulti(groupBy);
        const combinedGroupBy = handler.combinedGroupBy();

        let query = context.query;
        query     = this.ensureNotOnboardedStatusForCspCustomers(query);
        query     = this.appendCurrencyFilter(query, currency);

        const req                 = Models.BillingAggregationRequest.newInstance({
                                                                                     billing_source: billingSource,
                                                                                     ...combinedGroupBy,
                                                                                     filters   : query,
                                                                                     start_date: rangeExt.getMinAsDate(momentHelperKeepLocalTime),
                                                                                     end_date  : rangeExt.getMaxAsDate(momentHelperKeepLocalTime),
                                                                                     limit     : limit
                                                                                 });
        const valuesRaw           = await withComponentId(componentIdOf(context), () => this.api.billing.getDescriptionTrend(req));
        const timeRangeId         = TimeRanges.resolve(context.timeRange?.range)?.id;
        const isBillingRange      = TimeRange.isBillingTimeRange(timeRangeId);
        const isBillingRangeMonth = isBillingRange && granularity === Models.TrendGranularity.month;
        resolvedFilterOptions ||= await Promise.resolve(filterOptions);
        return valuesRaw.map(v => BillingTrendResultExtended.newInstance(resolvedFilterOptions, v, groupBy, isBillingRangeMonth));
    }

    protected async marginDescriptionTrend(filterOptions: FilterOptionsExtended,
                                           billingSource: string,
                                           groupBy: BillingGroupBy[],
                                           currency: Models.Currency,
                                           context: Models.DataContext,
                                           rangeExt: RangeSelectionExtended,
                                           granularity: Models.TrendGranularity,
                                           momentHelperKeepLocalTime: boolean, // todo: remove. This should be false and we should not have an option for it to be true
                                           limit: number,
                                           includeRetailCost?: boolean): Promise<BillingMarginResultExtended[]>
    {
        const [contractedSource, actualSource] = this.getBillingSources(billingSource);
        const [contractedCost, actualCost, retailCost] = await Promise.all([
                                                                       this.descriptionTrend(filterOptions,
                                                                                             contractedSource,
                                                                                             groupBy,
                                                                                             currency,
                                                                                             context,
                                                                                             rangeExt,
                                                                                             granularity,
                                                                                             momentHelperKeepLocalTime,
                                                                                             limit),
                                                                       this.descriptionTrend(filterOptions,
                                                                                             actualSource,
                                                                                             groupBy,
                                                                                             currency,
                                                                                             context,
                                                                                             rangeExt,
                                                                                             granularity,
                                                                                             momentHelperKeepLocalTime,
                                                                                             limit),
                                                                       includeRetailCost
                                                                           ? this.descriptionTrend(filterOptions,
                                                                                                   "raw_list_cost",
                                                                                                   groupBy,
                                                                                                   currency,
                                                                                                   context,
                                                                                                   rangeExt,
                                                                                                   granularity,
                                                                                                   momentHelperKeepLocalTime,
                                                                                                   limit)
                                                                           : Promise.resolve(null)
                                                                   ]);
        const handler = new BillingGroupByHandlerMulti(groupBy);
        return handler.computeMarginTrendValues(contractedCost, actualCost, billingSource, retailCost ?? undefined);
    }

    // Lazy twin of the eager account-type filter (prepareTrendPostProcess + applyTrendAccountTypeFilter):
    // derives the account-type grouping from the accounts present
    // in the response (one hierarchy batch) instead of enumerating the full service-account tree.
    private async filterByAccountTypeLazily(trends: BillingTrendResult[],
                                            requestedType: Models.ServiceAccountNativeDetailsBase | undefined,
                                            lazyOptions: LazyAccountTypeAggregationOptions,
                                            filterOptions: FilterOptionsExtended,
                                            groupBys: BillingGroupBy[],
                                            fetcher: ServiceAccountFetcher,
                                            billingSource?: string): Promise<BillingTrendResult[]>
    {
        const hierarchyFetcher = fetcher instanceof HierarchyServiceAccountListRecordFetcher && fetcher.includesParentInfo ? fetcher : null;
        if (!hierarchyFetcher)
        {
            console.error("filterByAccountTypeLazily requires a hierarchy fetcher with parent_accounts_info; returning unfiltered trends");
            return trends;
        }

        const serviceAccountIds = UtilsService.dedupeElements(trends.map(trend => trend.identity.model.service_account_id)
                                                                    .filter(id => !!id));

        const grouping = await resolveLazyAccountTypeGrouping(serviceAccountIds, requestedType, hierarchyFetcher, lazyOptions);
        lazyOptions.onTypesResolved?.({
                                          typeOptions  : grouping.typeOptions,
                                          effectiveType: grouping.effectiveType
                                      });

        if (!grouping.effectiveType)
        {
            // Strict callers (no fallback) expect an empty result when the requested type is absent;
            // with no requested type at all, mirror the eager no-op (no filtering).
            return requestedType && !lazyOptions.fallbackToFirstType ? [] : trends;
        }

        return this.aggregateBillingTrendResult(grouping.nodes, trends, filterOptions, groupBys, billingSource);
    }

    public collectSubtreesWithSelectedRootType(accountOptions: ControlOption<string>[],
                                               selectedAccountType: Models.ServiceAccountNativeDetailsBase,
                                               collectedAccounts: ServiceAccountHierarchyNode[] = []): ServiceAccountHierarchyNode[]
    {
        for (const accountOption of accountOptions)
        {
            const serviceAccountOption   = accountOption as ServiceAccountControlOption;
            const serviceAccountExtended = serviceAccountOption.serviceAccountExtended;
            const children               = serviceAccountOption.children || [];

            if (serviceAccountExtended)
            {
                const nativeDetails = serviceAccountExtended.details;

                // If this node matches, keep its full children structure
                if (nativeDetails instanceof selectedAccountType.constructor)
                {
                    const node = new ServiceAccountHierarchyNode(serviceAccountExtended.id, serviceAccountExtended.model);

                    for (const child of children)
                    {
                        node.addChild(this.mapSubTreesToRoot(child)); // Preserve structure
                    }
                    collectedAccounts.push(node);
                    continue; // Skip further processing, as we've already included it with full children
                }
            }

            // If this node doesn't match, process children recursively
            this.collectSubtreesWithSelectedRootType(children, selectedAccountType, collectedAccounts);
        }

        return collectedAccounts;
    }

    // Helper function to map any node to ServiceAccount (preserving structure)
    private mapSubTreesToRoot(accountOption: ControlOption<string>): ServiceAccountHierarchyNode
    {
        const serviceAccountOption   = accountOption as ServiceAccountControlOption;
        const serviceAccountExtended = serviceAccountOption.serviceAccountExtended;
        const node                   = new ServiceAccountHierarchyNode(serviceAccountExtended.id, serviceAccountExtended.model);

        if (serviceAccountOption.children)
        {
            for (const child of serviceAccountOption.children)
            {
                node.addChild(this.mapSubTreesToRoot(child));
            }
        }

        return node;
    }

    private aggregateBillingTrendResult(serviceAccounts: ServiceAccountHierarchyNode[],
                                        trendData: BillingTrendResult[],
                                        filterOptions: FilterOptionsExtended,
                                        groupBys: BillingGroupBy[],
                                        billingSource?: string): BillingTrendResult[]
    {
        const trendMap = new Map(trendData.map(trend => [
            trend.identity.model.service_account_id,
            trend
        ]));

        const results: BillingTrendResult[] = [];
        for (const account of serviceAccounts)
        {
            const accountId = account.id;
            if (!accountId) continue;

            let aggregatedData = trendMap.get(accountId);
            if (!aggregatedData)
            {
                const billingLineItemIdentity = Models.BillingLineItemIdentity.newInstance({service_account_id: accountId});
                const identity                = BillingLineItemExtended.newInstance(filterOptions, billingLineItemIdentity, groupBys);
                if (this.isCostMetricMargin(billingSource) || this.isProfitMargin(billingSource))
                {
                    aggregatedData = new BillingMarginResultExtended(identity,
                                                                     new BillingTrendPoints([]),
                                                                     new BillingTrendPoints([]),
                                                                     new BillingTrendPoints([]),
                                                                     new BillingTrendPoints([]),
                                                                     0,
                                                                     billingSource);
                }
                else
                {
                    aggregatedData = new BillingTrendResultExtended(identity, new BillingTrendPoints([]), new BillingTrendPoints([]), 0);
                }
                trendMap.set(accountId, aggregatedData);
            }

            if (account.children.length > 0)
            {
                this.processChildren(account, aggregatedData, trendMap, billingSource);
            }

            results.push(aggregatedData);
        }
        return results;
    }

    private processChildren(account: ServiceAccountHierarchyNode,
                            aggregatedData: BillingTrendResult,
                            trendMap: Map<string, BillingTrendResult>,
                            billingSource?: string)
    {
        for (const child of account.children)
        {
            const childData = trendMap.get(child.id);
            if (childData)
            {
                this.mergeTrends(aggregatedData, childData, billingSource);
                if (aggregatedData.forecastedTrend && childData.forecastedTrend) this.mergeTrends(aggregatedData, childData, billingSource);
                aggregatedData.numAccounts += childData.numAccounts;
            }

            if (child.children.length > 0)
            {
                this.processChildren(child, aggregatedData, trendMap, billingSource);
            }
        }
    }

    // Function to merge trend points from child to parent
    public mergeTrends(parentData: BillingTrendResult,
                       childData: BillingTrendResult,
                       billingSource?: string)
    {
        const mergePointData = (parentPoints: BillingTrendPoint[],
                                childPoints: BillingTrendPoint[],
                                parentTotal: number,
                                childTotal: number): number =>
        {
            const pointMap = new Map<number, BillingTrendPoint>();

            for (const point of parentPoints)
            {
                pointMap.set(point.timestamp.getTime(), point);
            }

            for (const childPoint of childPoints)
            {
                const time     = childPoint.timestamp.getTime();
                const existing = pointMap.get(time);
                if (existing)
                {
                    existing.value += childPoint.value;
                }
                else
                {
                    const newPoint = new BillingTrendPoint(childPoint.value, childPoint.timestamp, childPoint.billingTimestamp);
                    parentPoints.push(newPoint);
                    pointMap.set(time, newPoint);
                }
            }

            //Ensure the trend points are sorted by timestamp
            parentPoints.sort((a,
                               b) => a.timestamp.getTime() - b.timestamp.getTime());

            return parentTotal + childTotal;
        };

        const calculatePercentagePointData = (parentData: BillingTrendResult,
                                              childData: BillingTrendResult): number =>
        {
            const createPointMap = (points: BillingTrendPoint[]): Map<number, BillingTrendPoint> =>
            {
                const map = new Map<number, BillingTrendPoint>();
                for (const point of points)
                {
                    map.set(point.timestamp.getTime(), point);
                }
                return map;
            };

            const marginPointMap       = createPointMap(parentData.margin.points);
            const contractedPointMap   = createPointMap(parentData.contractedCost.points);
            const profitMarginPointMap = createPointMap(parentData.profitMargin.points);

            for (const childPoint of childData.profitMargin.points)
            {
                const time = childPoint.timestamp.getTime();

                const marginPoint       = marginPointMap.get(time);
                const contractedPoint   = contractedPointMap.get(time);
                const profitMarginPoint = profitMarginPointMap.get(time);

                if (profitMarginPoint && marginPoint && contractedPoint)
                {
                    profitMarginPoint.value = contractedPoint.value !== 0 ? (marginPoint.value / contractedPoint.value) * 100 : 0;
                }
                else
                {
                    const newPoint = new BillingTrendPoint(childPoint.value, childPoint.timestamp, childPoint.billingTimestamp);
                    parentData.profitMargin.points.push(newPoint);
                    marginPointMap.set(time, newPoint);
                    contractedPointMap.set(time, newPoint);
                }
            }

            //Ensure the trend points are sorted by timestamp
            parentData.profitMargin.points.sort((a,
                                                 b) => a.timestamp.getTime() - b.timestamp.getTime());

            return parentData.contractedCost.total !== 0 ? (parentData.margin.total / parentData.contractedCost.total) * 100 : 0;
        };

        if (this.isCostMetricMargin(billingSource) || this.isProfitMargin(billingSource))
        {
            parentData.contractedCost.total = mergePointData(parentData.contractedCost.points,
                                                             childData.contractedCost.points,
                                                             parentData.contractedCost.total,
                                                             childData.contractedCost.total);

            parentData.sourceCost.total = mergePointData(parentData.sourceCost.points,
                                                         childData.sourceCost.points,
                                                         parentData.sourceCost.total,
                                                         childData.sourceCost.total);

            parentData.margin.total = mergePointData(parentData.margin.points,
                                                     childData.margin.points,
                                                     parentData.margin.total,
                                                     childData.margin.total);

            parentData.profitMargin.total = calculatePercentagePointData(parentData, childData);
        }
        else
        {
            parentData.trend.total = mergePointData(parentData.trend.points,
                                                    childData.trend.points,
                                                    parentData.trend.total,
                                                    childData.trend.total);

            if (parentData.forecastedTrend && childData.forecastedTrend)
            {
                parentData.forecastedTrend.total = mergePointData(parentData.forecastedTrend.points,
                                                                  childData.forecastedTrend.points,
                                                                  parentData.forecastedTrend.total,
                                                                  childData.forecastedTrend.total);
            }
        }
    }

    public aggregateBillingResult(serviceAccountHierarchyNodes: ServiceAccountHierarchyNode[],
                                  aggregateData: BillingAggregationResult[],
                                  filterOptions: FilterOptionsExtended,
                                  billingSource: string): BillingAggregationResult[]
    {

        const aggregateMap = new Map(aggregateData.map(aggregate => [
            aggregate.identity.model.service_account_id,
            aggregate
        ]));

        const results: BillingAggregationResult[] = [];

        for (const account of serviceAccountHierarchyNodes)
        {
            const accountId = account.id;
            if (!accountId) continue;

            let aggregatedBillingData = aggregateMap.get(accountId);
            if (!aggregatedBillingData)
            {
                const billingLineItemIdentity = Models.BillingLineItemIdentity.newInstance({service_account_id: accountId});
                const identity                = BillingLineItemExtended.newInstance(filterOptions, billingLineItemIdentity, null);
                aggregatedBillingData         = this.isCostMetricMargin(billingSource) ? new BillingMarginAggResultExtended(identity,
                                                                                                                            0,
                                                                                                                            0,
                                                                                                                            0,
                                                                                                                            0,
                                                                                                                            0,
                                                                                                                            billingSource) : new BillingAggregationResultExtended(identity, 0, 0);
                aggregateMap.set(accountId, aggregatedBillingData);
            }

            if (account.children.length > 0)
            {
                this.processAggregateChildren(account, aggregatedBillingData, aggregateMap);
            }

            results.push(aggregatedBillingData);
        }
        return results;
    }

    private processAggregateChildren(account: ServiceAccountHierarchyNode,
                                     aggregatedData: BillingAggregationResult,
                                     aggregateMap: Map<string, BillingAggregationResult>)
    {
        for (const child of account.children)
        {
            const childData = aggregateMap.get(child.id);
            if (childData)
            {
                aggregatedData.numAccounts += childData.numAccounts;
                aggregatedData.value += childData.value;
                aggregatedData.contractedCost += childData.contractedCost;
                aggregatedData.sourceCost += childData.sourceCost;
                aggregatedData.margin += childData.margin;
            }

            if (child.children.length > 0)
            {
                this.processAggregateChildren(child, aggregatedData, aggregateMap);
            }
        }

        if (aggregatedData.contractedCost !== 0)
        {
            aggregatedData.profitMargin = (aggregatedData.margin / aggregatedData.contractedCost) * 100;
        }
        else
        {
            aggregatedData.profitMargin = 0;
        }
    }

    public async lineItemSummary(key: GroupBySelector,
                                 serviceAccountScope?: BillingServiceAccountScope,
                                 isKubernetes?: boolean,
                                 platform?: Models.Platforms): Promise<Models.ServiceAccountKeyValues[]>
    {
        if (serviceAccountScope?.service_account_ids?.length === 0 && !serviceAccountScope.descendant_service_account_query) return [];

        const parsedGroupByValue = UtilsService.parseEnumValue<Models.BillingGroupBySelector>(Models.BillingGroupBySelector, key);
        const request            = Models.BillingLineItemSummaryRequest.newInstance({
                                                                                        key         : Models.BillingGroupByDetails.newInstance({selector: parsedGroupByValue}),
                                                                                        for_platform: !!isKubernetes,
                                                                                        platform,
                                                                                        ...serviceAccountScope
                                                                                    });
        return this.api.billing.getLineItemSummary(request);
    }

    public async getAllKeyTypes(key: GroupBySelector,
                                serviceAccountScope?: BillingServiceAccountScope,
                                isKubernetes?: boolean,
                                platform?: Models.Platforms): Promise<string[]>
    {
        const results = await this.lineItemSummary(key, serviceAccountScope, isKubernetes, platform);

        const seenTypes = new Set<string>();
        for (const result of results)
        {
            for (const keyValue of result.values_by_key)
            {
                for (const value of keyValue.values) seenTypes.add(value);
            }
        }

        return Array.from(seenTypes);
    }

    public async getAllMasterDataList(serviceNameData: string[]): Promise<Models.MasterDataCategoryListResponse>
    {
        const request = Models.MasterDataCategoryListRequest.newInstance({
                                                                             list_context: Models.ListContext.newInstance({
                                                                                                                              batch_size  : 1000,
                                                                                                                              batch_offset: 0,
                                                                                                                              total       : 0
                                                                                                                          }),
                                                                             filters     : Models.MasterDataCategoryFilters.newInstance({
                                                                                                                                            service_code: serviceNameData
                                                                                                                                        })
                                                                         });

        return this.api.masterData.listMasterDataCategories(request);
    }

    public getUniqueCategories(items: Models.MasterDataCategoryDetails[],
                               filterSet?: Set<string>): ControlOption<string>[]
    {
        const category                        = new Set<string>();
        const result: ControlOption<string>[] = [];

        for (const item of items)
        {
            if (!filterSet || filterSet.has(item.service_type))
            {
                if (!category.has(item.generalized_category))
                {
                    category.add(item.generalized_category);
                    result.push(new ControlOption(item.generalized_category, item.generalized_category));
                }
            }
        }
        result.sort((a,
                     b) => UtilsService.compareStrings(a.label, b.label, true));

        return result;
    }

    public async buildK8sTags(includeValues: boolean,
                              serviceAccountScope?: BillingServiceAccountScope,
                              kinds?: string[],
                              tagKeys?: string[],
                              platform?: Models.Platforms): Promise<Lookup<Lookup<Lookup<string[]>>>>
    {
        platform       = this.platformType?.resolve(platform) ?? platform;
        const filter   = Models.BillingPlatformTagKeysRequest.newInstance({
                                                                              include_values: includeValues,
                                                                              kinds         : kinds,
                                                                              tag_keys      : tagKeys,
                                                                              for_platform  : true,
                                                                              ...serviceAccountScope,
                                                                              ...(platform ? {platform} : {})
                                                                          });
        const cacheKey = this.getK8sTagsCacheKey(filter);
        let cached     = this.k8sTagValuesCache.get(cacheKey);
        if (cached) return cached;

        cached = (async () =>
        {
            const tagValues                                = await this.api.billing.getPlatformServiceAccountTagsDetails(filter);
            const result: Lookup<Lookup<Lookup<string[]>>> = {};
            for (const ch of tagValues)
            {
                const {
                          service_account_id,
                          values_by_key
                      } = ch;

                if (!result[service_account_id])
                {
                    result[service_account_id] = {};
                }

                for (const entry of values_by_key)
                {
                    const {
                              kind,
                              key,
                              values
                          } = entry;

                    if (!result[service_account_id][kind])
                    {
                        result[service_account_id][kind] = {};
                    }

                    result[service_account_id][kind]![key] = values;
                }
            }

            return result;
        })();
        this.k8sTagValuesCache.set(cacheKey, cached);

        return cached;
    }

    public async getAllK8sTagsKey(serviceAccountScope?: BillingServiceAccountScope,
                                  kinds?: string[],
                                  platform?: Models.Platforms): Promise<Lookup<Lookup<Lookup<string[]>>>>
    {
        return this.buildK8sTags(false, serviceAccountScope, kinds, undefined, platform);
    }

    public async getAllK8sTags(serviceAccountScope?: BillingServiceAccountScope,
                               kinds?: string[],
                               tagKeys?: string[],
                               platform?: Models.Platforms): Promise<Lookup<Lookup<Lookup<string[]>>>>
    {
        return this.buildK8sTags(true, serviceAccountScope, kinds, tagKeys, platform);
    }

    public async getAllK8sTagsKinds(serviceAccountScope?: BillingServiceAccountScope,
                                    platform?: Models.Platforms): Promise<ControlOption<string>[]>
    {
        const tagKeys = await this.getAllK8sTagsKey(serviceAccountScope, undefined, platform);
        const kinds   = new Set<string>();

        const cloudAccountIds = serviceAccountScope?.descendant_service_account_query ? Object.keys(tagKeys) : serviceAccountScope?.service_account_ids || Object.keys(tagKeys);
        for (const id of cloudAccountIds)
        {
            const accountTags = tagKeys[id];
            if (!accountTags) continue;

            for (const kind of Object.keys(accountTags))
            {
                kinds.add(kind);
            }
        }

        return Array.from(kinds)
                    .map(kind => new ControlOption(kind, kind))
                    .sort((a,
                           b) => UtilsService.compareStrings(a.label, b.label, true));
    }

    public async getAllK8sTagsAsOptions(kind: string,
                                        serviceAccountScope?: BillingServiceAccountScope,
                                        platform?: Models.Platforms): Promise<ControlOption<string>[]>
    {
        const tagKeys   = await this.getAllK8sTagsKey(serviceAccountScope, kind ? [kind] : undefined, platform);
        const tagKeySet = new Set<string>();

        const cloudAccountIds = serviceAccountScope?.descendant_service_account_query ? Object.keys(tagKeys) : serviceAccountScope?.service_account_ids || Object.keys(tagKeys);
        for (const accountId of cloudAccountIds)
        {
            const accountTags = tagKeys[accountId];
            if (!accountTags) continue;

            const kindTags = accountTags[kind];
            if (!kindTags) continue;

            for (const tagKey of Object.keys(kindTags))
            {
                tagKeySet.add(tagKey);
            }
        }

        return Array.from(tagKeySet)
                    .map(tagKey => new ControlOption(tagKey, tagKey))
                    .sort((a,
                           b) => UtilsService.compareStrings(a.label, b.label, true));
    }

    public async getK8sTagValues(kind: string,
                                 key: string,
                                 serviceAccountScope?: BillingServiceAccountScope,
                                 platform?: Models.Platforms): Promise<ControlOption<string>[]>
    {
        if (kind == null || !key)
        {
            return [];
        }

        const tagKeys     = await this.getAllK8sTags(serviceAccountScope, [kind], [key], platform);
        const tagValueSet = new Set<string>();

        const accountIds = serviceAccountScope?.descendant_service_account_query ? Object.keys(tagKeys) : serviceAccountScope?.service_account_ids || Object.keys(tagKeys);

        for (const accountId of accountIds)
        {
            const accountTags = tagKeys[accountId];
            if (!accountTags) continue;

            const kindTags = accountTags[kind];
            if (!kindTags) continue;

            const values = kindTags[key];
            if (!values) continue;

            for (const value of values)
            {
                tagValueSet.add(value);
            }
        }

        return Array.from(tagValueSet)
                    .map(value => new ControlOption(value, value))
                    .sort((a,
                           b) => UtilsService.compareStrings(a.label, b.label, true));
    }

    @Memoizer
    public async getAllTags(): Promise<Lookup<Set<string>>>
    {
        const filter = Models.BillingTagKeysRequest.newInstance({
                                                                    include_values: false
                                                                });

        const tagValues = await this.api.billing.getTagKeysByServiceAccounts(filter);

        const result: Lookup<Set<string>> = {};

        for (const item of tagValues)
        {
            const tags = new Set<string>();
            for (const keyValue of item.values_by_key)
            {
                tags.add(keyValue.key);
            }

            result[item.service_account_id] = tags;
        }

        return result;
    }

    public async getAllTagsAsOptions(serviceAccountScope?: BillingServiceAccountScope | null): Promise<ControlOption<string>[]>
    {
        if (serviceAccountScope?.descendant_service_account_query)
        {
            const tagValues = await this.getScopedTagKeysByServiceAccounts(Models.BillingTagKeysRequest.newInstance({
                                                                                                                        include_values: false,
                                                                                                                        ...serviceAccountScope
                                                                                                                    }));
            return this.flattenServiceAccountKeyNames(tagValues);
        }

        const tagKeys = await this.getAllTags();

        const tags          = new Set<string>();
        let cloudAccountIds = serviceAccountScope?.service_account_ids;

        if (!cloudAccountIds) cloudAccountIds = Object.keys(tagKeys);

        for (const id of cloudAccountIds)
        {
            const accountTags = tagKeys[id];
            if (!accountTags) continue;

            for (const tag of accountTags)
            {
                tags.add(tag);
            }
        }

        return Array.from(tags)
                    .map(item => new ControlOption(item, item))
                    .sort((a,
                           b) => UtilsService.compareStrings(a.label, b.label, true));
    }

    public async getTagKeyOptionsForAccounts(serviceAccountIds: string[]): Promise<ControlOption<string>[]>
    {
        const tagValues = await this.getScopedTagKeysByServiceAccounts(Models.BillingTagKeysRequest.newInstance({
                                                                                                                    include_values     : false,
                                                                                                                    service_account_ids: serviceAccountIds
                                                                                                                }));
        return this.flattenServiceAccountKeyNames(tagValues);
    }

    public async getTagValues(key: string,
                              serviceAccountScope?: BillingServiceAccountScope | null): Promise<string[]>
    {
        if (serviceAccountScope?.descendant_service_account_query)
        {
            const tagValues = await this.getScopedTagKeysByServiceAccounts(Models.BillingTagKeysRequest.newInstance({
                                                                                                                        include_values: true,
                                                                                                                        tag_keys      : [key],
                                                                                                                        ...serviceAccountScope
                                                                                                                    }));
            return this.flattenServiceAccountValues(tagValues);
        }

        // Check if the key is already cached
        let accountTagValues = this.tagValuesCache.get(key);

        if (!accountTagValues)
        {
            const filter = Models.BillingTagKeysRequest.newInstance({
                                                                        include_values: true,
                                                                        tag_keys      : [key]
                                                                    });

            const apiTagValues = await this.api.billing.getTagKeysByServiceAccounts(filter);
            accountTagValues   = {};
            for (const item of apiTagValues)
            {
                const itemTagValues = new Set<string>();
                for (const keyItem of item.values_by_key)
                {
                    for (const value of keyItem.values)
                    {
                        itemTagValues.add(value);
                    }
                }
                accountTagValues[item.service_account_id] = itemTagValues;
            }

            this.tagValuesCache.set(key, accountTagValues);
        }

        let cloudAccountIds = serviceAccountScope?.service_account_ids;
        if (!cloudAccountIds) cloudAccountIds = Object.keys(accountTagValues);

        const values = new Set<string>();
        for (const id of cloudAccountIds)
        {
            const accountValues = accountTagValues[id];
            if (!accountValues) continue;

            for (const value of accountValues)
            {
                values.add(value);
            }
        }

        const result = Array.from(values);
        result.sort();

        return result;
    }

    public async getAllExtraKeys(isK8Config?: boolean,
                                 serviceAccountScope?: BillingServiceAccountScope,
                                 platform?: Models.Platforms): Promise<Lookup<Set<string>>>
    {
        const filter = Models.BillingExtraKeysRequest.newInstance({
                                                                      include_values: false,
                                                                      ...serviceAccountScope
                                                                  });
        if (platform) filter.platform = platform;
        else if (isK8Config) filter.for_platform = true;

        const extraKeyValues = await this.api.billing.getExtraKeysByServiceAccounts(filter);

        const result: Lookup<Set<string>> = {};

        for (const item of extraKeyValues)
        {
            const extraKeys = new Set<string>();
            for (const keyValue of item.values_by_key)
            {
                extraKeys.add(keyValue.key);
            }

            result[item.service_account_id] = extraKeys;
        }

        return result;
    }

    public async getAllExtraKeysAsOptions(cloudAccountIds: string[],
                                          focusCloud: Models.CloudServiceName,
                                          flavor: Models.DashboardFlavor,
                                          allowComputed?: boolean,
                                          isK8Config: boolean = false,
                                          serviceAccountScope?: BillingServiceAccountScope,
                                          platform?: Models.Platforms): Promise<ControlOption<string>[]>
    {
        const extraKeys   = await this.getAllExtraKeys(isK8Config, serviceAccountScope, platform);
        const accountIds  = serviceAccountScope?.descendant_service_account_query || !cloudAccountIds?.length ? Object.keys(extraKeys) : cloudAccountIds;
        const excludeKeys = isK8Config && flavor ? new Set([
                                                               "pod",
                                                               "pod_id",
                                                               "container"
                                                           ]) : null;
        const keys        = new Set<string>();

        for (const id of accountIds)
        {
            const accountKeys = extraKeys[id];
            if (!accountKeys) continue;

            for (const key of accountKeys)
            {
                if (!excludeKeys?.has(key))
                {
                    keys.add(key);
                }
            }
        }

        const options   = [];
        const uniqueKey = new Set<string>();

        for (const key of keys)
        {
            const computedDimensions = allowComputed && FinopsFocusComputing.getComputedDimensions(key, focusCloud) || [];
            for (const computed of computedDimensions)
            {
                if (!uniqueKey.has(computed))
                {
                    options.push(BillingService.newKeyOption(computed, focusCloud));
                    uniqueKey.add(computed);
                }
            }

            if (!uniqueKey.has(key))
            {
                options.push(BillingService.newKeyOption(key, focusCloud));
                uniqueKey.add(key);
            }
        }

        return options.sort((a,
                             b) => UtilsService.compareStrings(a.label, b.label, true));
    }

    private static newKeyOption(key: string,
                                focusCloud: Models.CloudServiceName): ControlOption<string>
    {
        let label = FinopsFocusMapping.getDimensionTerminology(key, focusCloud) || TitleCaseFormatPipe.format(key);
        return new ControlOption(key, label);
    }

    public async getDimensionValues(key: string,
                                    isK8Config?: boolean,
                                    serviceAccountScope?: BillingServiceAccountScope,
                                    platform?: Models.Platforms): Promise<Lookup<Set<string>>>
    {
        // Check if the key is already cached
        const cacheKey       = this.getExtraKeyValuesCacheKey(key, isK8Config, serviceAccountScope, platform);
        let accountKeyValues = this.extraKeyValuesCache.get(cacheKey);
        if (!accountKeyValues)
        {
            accountKeyValues = {};
            if (FinopsFocusComputing.isComputed(key))
            {
                // use cloud as key
                const appendComputedDimensions = (cloud: Models.CloudServiceName) =>
                {
                    const values = FinopsFocusComputing.getComputedConditions(key, cloud);
                    if (values) accountKeyValues[cloud] = new Set(values);
                };
                appendComputedDimensions(Models.CloudServiceName.AWS);
                appendComputedDimensions(Models.CloudServiceName.Azure);
                appendComputedDimensions(Models.CloudServiceName.GCP);
                appendComputedDimensions(Models.CloudServiceName.OCI);
            }
            else
            {
                // use cloud account id as key
                const filter = Models.BillingExtraKeysRequest.newInstance({
                                                                              include_values: true,
                                                                              extra_keys    : [key],
                                                                              ...serviceAccountScope,
                                                                              ...(platform ? {platform} : (isK8Config && {for_platform: isK8Config}))
                                                                          });

                const extraKeyValues = await this.api.billing.getExtraKeysByServiceAccounts(filter);
                for (const item of extraKeyValues)
                {
                    const values = new Set<string>();
                    for (const key of item.values_by_key)
                    {
                        for (const value of key.values) values.add(value);
                    }
                    accountKeyValues[item.service_account_id] = values;
                }
            }
            this.extraKeyValuesCache.set(cacheKey, accountKeyValues);
        }

        return accountKeyValues;
    }

    public async getExtraDimensionValueOptions(dimension: string,
                                               filterOptions: FilterOptionsExtended | null,
                                               cloudAccountIds: string[],
                                               focusCloud: Models.CloudServiceName,
                                               isK8Config?: boolean,
                                               serviceAccountScope?: BillingServiceAccountScope,
                                               platform?: Models.Platforms): Promise<ControlOption<string>[]>
    {
        const dimensionValues = await this.getDimensionValues(dimension, isK8Config, serviceAccountScope, platform);
        const isComputed      = FinopsFocusComputing.isComputed(dimension);

        let keys: string[] = isComputed ? [
            Models.CloudServiceName.AWS,
            Models.CloudServiceName.Azure,
            Models.CloudServiceName.GCP,
            Models.CloudServiceName.OCI
        ] : serviceAccountScope?.descendant_service_account_query ? Object.keys(dimensionValues) : cloudAccountIds;
        if (!keys?.length) keys = Object.keys(dimensionValues);

        if (FinopsFocusGrouping.hasGroupingRules(UtilsService.parseEnumValue(FinopsFocusDimension, dimension)))
        {
            const mappedValuesByCloud: Lookup<Lookup<Set<string>>, Models.CloudServiceName> = {};
            for (const key of keys)
            {
                const cloud = isComputed ? <Models.CloudServiceName>key : filterOptions?.resolveCloudServiceName(key);
                if (!cloud) continue;

                const valueMapping = FinopsFocusGrouping.getCloudConditionGrouping(<any>dimension, cloud);
                const values       = dimensionValues[key];
                if (values?.size)
                {
                    const mappedToOriginals = LookupHelpers.ensureBaseState(mappedValuesByCloud, cloud, () => <Lookup<Set<string>>>{});
                    for (const value of values)
                    {
                        LookupHelpers.ensureBaseState(mappedToOriginals, valueMapping(value), () => new Set())
                                     .add(value);
                    }
                }
            }

            return FinopsFocusGrouping.generateGroupedOptions(mappedValuesByCloud, focusCloud, dimension);
        }

        const values = new Set<string>();
        for (const id of keys ?? [])
        {
            for (const value of dimensionValues[id] || []) values.add(value);
        }

        const options = [];
        for (const value of values) options.push(new ControlOption(value, value));
        ControlOption.sortByLabel(options, true);
        return options;
    }

    public requiresFilterOptionsForExtraDimensionValueOptions(dimension: string): boolean
    {
        return !FinopsFocusComputing.isComputed(dimension)
               && FinopsFocusGrouping.hasGroupingRules(UtilsService.parseEnumValue(FinopsFocusDimension, dimension));
    }

    private flattenServiceAccountKeyNames(tagValues: Models.ServiceAccountKeyValues[]): ControlOption<string>[]
    {
        const values = new Set<string>();
        for (const item of tagValues)
        {
            for (const keyValue of item.values_by_key)
            {
                values.add(keyValue.key);
            }
        }

        return Array.from(values)
                    .map(item => new ControlOption(item, item))
                    .sort((a,
                           b) => UtilsService.compareStrings(a.label, b.label, true));
    }

    private getScopedTagKeysByServiceAccounts(filter: Models.BillingTagKeysRequest): Promise<Models.ServiceAccountKeyValues[]>
    {
        const cacheKey = JSON.stringify({
                                            ...this.getServiceAccountScopeCacheKeyParts(filter),
                                            include_values: filter.include_values,
                                            tag_keys      : this.getSortedCacheValues(filter.tag_keys)
                                        });

        let cached = this.scopedTagValuesCache.get(cacheKey);
        if (!cached)
        {
            cached = this.api.billing.getTagKeysByServiceAccounts(filter);
            this.scopedTagValuesCache.set(cacheKey, cached);
        }

        return cached;
    }

    private getK8sTagsCacheKey(filter: Models.BillingPlatformTagKeysRequest): string
    {
        return JSON.stringify({
                                  ...this.getServiceAccountScopeCacheKeyParts(filter),
                                  include_values: filter.include_values,
                                  kinds         : this.getSortedCacheValues(filter.kinds),
                                  tag_keys      : this.getSortedCacheValues(filter.tag_keys),
                                  platform      : filter.platform
                              });
    }

    private getExtraKeyValuesCacheKey(key: string,
                                      isK8Config?: boolean,
                                      serviceAccountScope?: BillingServiceAccountScope,
                                      platform?: Models.Platforms): string
    {
        return JSON.stringify({
                                  ...this.getServiceAccountScopeCacheKeyParts(serviceAccountScope),
                                  key,
                                  isK8Config: !!isK8Config,
                                  platform
                              });
    }

    private getServiceAccountScopeCacheKeyParts(serviceAccountScope?: BillingServiceAccountScope): Record<string, unknown>
    {
        return {
            service_account_ids             : this.getSortedCacheValues(serviceAccountScope?.service_account_ids),
            descendant_service_account_query: this.getDescendantServiceAccountQueryCacheKeyParts(serviceAccountScope?.descendant_service_account_query)
        };
    }

    private getDescendantServiceAccountQueryCacheKeyParts(query?: Models.DescendantServiceAccountQuery): Record<string, unknown> | undefined
    {
        if (!query) return undefined;

        return {
            service_account_ids         : this.getSortedCacheValues(query.service_account_ids),
            exclude_service_account_ids : this.getSortedCacheValues(query.exclude_service_account_ids),
            include_billing_as_parent   : query.include_billing_as_parent,
            include_resource_as_parent  : query.include_resource_as_parent,
            include_commercial_as_parent: query.include_commercial_as_parent
        };
    }

    private getSortedCacheValues(values?: string[]): string[] | undefined
    {
        return values?.slice()
                     .sort();
    }

    private flattenServiceAccountValues(tagValues: Models.ServiceAccountKeyValues[]): string[]
    {
        const values = new Set<string>();
        for (const item of tagValues)
        {
            for (const keyValue of item.values_by_key)
            {
                for (const value of keyValue.values || []) values.add(value);
            }
        }

        const result = Array.from(values);
        result.sort();
        return result;
    }

    public getDateRangeFromTrendResult(trendResult: BillingTrendResult[]): RangeSelectionExtended
    {
        const allTimestamps = trendResult
            .flatMap(trendExt => trendExt.trend.points.map(point => point.timestamp.getTime()));

        const minTimestamp = MomentHelper.parseAsUTC(Math.min(...allTimestamps));
        const maxTimestamp = MomentHelper.parseAsUTC(Math.max(...allTimestamps))
                                         .add(1, "day");
        return new RangeSelectionExtended(RangeSelectionExtended.newRange(MomentHelper.toLocalDate(minTimestamp), MomentHelper.toLocalDate(maxTimestamp)));
    }

    public async loadDimensionOptions(): Promise<Lookup<DimensionExtended>>
    {
        const dimensions = await this.dimensions.getDimensions();
        return UtilsService.extractLookup(dimensions);
    }

    public isProfitMargin(billingSource: string): boolean
    {
        return billingSource === BillingSourceType.ProfitMargin || billingSource === BillingSourceType.PartnerProfitMargin;
    }

    public isCustomerMargin(billingSource: string): boolean
    {
        return billingSource === BillingSourceType.MarginValue || billingSource === BillingSourceType.ProfitMargin;
    }

    public isPartnerMargin(billingSource: string): boolean
    {
        return billingSource === BillingSourceType.PartnerMarginValue || billingSource === BillingSourceType.PartnerProfitMargin;
    }

    public isCostMetricMargin(billingSource: string): boolean
    {
        return this.isCustomerMargin(billingSource) || this.isPartnerMargin(billingSource);
    }

    public getMarginLabel(billingSource: string): string
    {
        const isPartnerMargin = this.isPartnerMargin(billingSource);
        const isProfitMargin  = this.isProfitMargin(billingSource);

        if (isPartnerMargin)
        {
            return isProfitMargin ? BillingSourceName.PartnerProfitMargin : BillingSourceName.PartnerMarginValue;
        }
        else
        {
            return isProfitMargin ? BillingSourceName.ProfitMargin : BillingSourceName.MarginValue;
        }
    }

    public getBillingSourceType(billingSource: string): BillingSourceType
    {
        const isPartnerMargin = this.isPartnerMargin(billingSource);
        const isProfitMargin  = this.isProfitMargin(billingSource);

        if (isPartnerMargin)
        {
            return isProfitMargin ? BillingSourceType.PartnerProfitMargin : BillingSourceType.PartnerMarginValue;
        }
        else
        {
            return isProfitMargin ? BillingSourceType.ProfitMargin : BillingSourceType.MarginValue;
        }
    }

    public getMarginValueLabel(billingSource: string): string
    {
        const isPartnerMargin = this.isPartnerMargin(billingSource);
        return isPartnerMargin ? BillingSourceName.PartnerMarginValue : BillingSourceName.MarginValue;
    }

    public getProfitMarginLabel(billingSource: string): string
    {
        const isPartnerMargin = this.isPartnerMargin(billingSource);
        return isPartnerMargin ? BillingSourceName.PartnerProfitMargin : BillingSourceName.ProfitMargin;
    }

    public getMetricDetails(cloud: ServiceProviderName,
                            billingSource: string): [string, string]
    {
        const focusKey                     = FinopsDimensions.getDimensionKey(cloud) ?? "multi";
        const [customerSource, sourceCost] = this.getBillingSources(billingSource);
        const customerMetric               = costMetricLookup[<CostMetricId>customerSource]?.[focusKey];
        const sourceCostMetric             = costMetricLookup[<CostMetricId>sourceCost]?.[focusKey];

        return [
            customerMetric,
            sourceCostMetric
        ];
    }

    public sortByFirstGroup<T extends CostTrendFlat | CostTrendAggregation>(rows: T[],
                                                                            groupBy: BillingGroupBy[],
                                                                            aggregationData: CostTrendAggregation[]): T[]
    {
        const firstGroupBy = groupBy[0];
        const FALLBACK_KEY = "";

        aggregationData.sort((a,
                              b) => UtilsService.compareNumbers(a.total, b.total, false));

        const groupedRows = new Map<string, T[]>();

        rows?.forEach(obj =>
                      {
                          const rawKey =
                                    firstGroupBy.selector === GroupBySelector.extras
                                        ? obj.extraValue?.[firstGroupBy.key]
                                        : obj[firstGroupBy.selector];

                          const key = (rawKey ?? FALLBACK_KEY).toString();

                          (groupedRows.get(key) ?? groupedRows.set(key, [])
                                                              .get(key)!).push(obj);
                      });

        const ordered: T[] = [];
        const seen         = new Set<string>();

        for (const agg of aggregationData)
        {
            const rawAggKey = agg[firstGroupBy.selector];
            const aggKey    = (rawAggKey ?? FALLBACK_KEY).toString();

            const bucket = groupedRows.get(aggKey);
            if (bucket?.length) ordered.push(...bucket);
            seen.add(aggKey);
        }

        for (const [k, bucket] of groupedRows)
        {
            if (!seen.has(k) && bucket?.length) ordered.push(...bucket);
        }

        return ordered;
    }
}

export interface BillingAggregationOptions
{
    limit?: number;
    forecastDuration?: ForecastDuration;
    computeForecast?: boolean;
    selectedAccountType?: Models.ServiceAccountNativeDetailsBase;
    // Lazy service-account evaluation: derive the account-type grouping from the response accounts
    // (one hierarchy batch) instead of enumerating the full service-account tree.
    lazyAccountTypeAggregation?: LazyAccountTypeAggregationOptions;
    fillEmptyTrendPoints?: boolean;
    fetcher?: ServiceAccountFetcher;
    domain?: AppDomainContext;
    platformAsKubernetes?: boolean;
    platform?: Models.Platforms;
    computeK8ScopedCost?: boolean;
    excludeDimensionCostCenters?: boolean;
    isMultiCloudSaas?: boolean;
    includeRetailCost?: boolean;
    useActualValuesBeforeForecast?: boolean;
    forecastSplitTime?: number;
    actualDataStartTime?: number;
    actualDataEndTime?: number;
    fiscalStartMonth?: number;
    useRangeExtDate?: boolean; // do not use; todo: remove
    granularity?: Models.TrendGranularity;
}

export class BillingAggregationResultExtended implements BillingAggregationResult
{
    public static newInstance(filterOptions: FilterOptionsExtended,
                              model: Models.BillingAggregationResult,
                              groupBys: BillingGroupBy[]): BillingAggregationResultExtended
    {
        const identity = BillingLineItemExtended.newInstance(filterOptions, model.identity, groupBys);
        return new BillingAggregationResultExtended(identity, model.value, 1);
    }

    constructor(public readonly identity: BillingLineItemExtended,
                public value: number,
                public numAccounts: number)
    {
    }

    public get cloud(): Models.CloudServiceName
    {
        return this.identity?.cloud;
    }

    public get serviceAccountName(): string
    {
        return this.identity?.cloudAccountName;
    }

    public get serviceAccountId(): string
    {
        return this.identity?.model?.service_account_id;
    }
}

// Extended cost and usage aggregate result - adds the usage/rate values to the base cost aggregation.
export class BillingCostAndUsageAggregationResultExtended extends BillingAggregationResultExtended
{
    constructor(identity: BillingLineItemExtended,
                value: number,
                numAccounts: number,
                public readonly usage: number,
                public readonly rate: number)
    {
        super(identity, value, numAccounts);
    }
}

// Extended margin aggregate result
export class BillingMarginAggResultExtended extends BillingAggregationResultExtended implements BillingAggregationResult
{
    constructor(identity: BillingLineItemExtended,
                public sourceCost: number,
                public contractedCost: number,
                public margin: number,
                public readonly profitMargin: number,
                numAccounts: number,
                billingSource: string,
                public retailCost?: number)
    {
        let values: number;
        switch (billingSource)
        {
            case BillingSourceType.MarginValue:
            case BillingSourceType.PartnerMarginValue:
                values = margin;
                break;

            case BillingSourceType.ProfitMargin:
            case BillingSourceType.PartnerProfitMargin:
                values = profitMargin;
                break;
        }
        super(identity, values, numAccounts);
    }
}

export class BillingK8sAggregationResultExtended extends BillingAggregationResultExtended implements BillingAggregationResult
{
    constructor(identity: BillingLineItemExtended,
                value: number,
                numAccounts: number,
                public readonly totalCloudSpend: number,
                public readonly scopedCost: number,
                public readonly resourceServiceAccountId?: string)
    {
        super(identity, value, numAccounts);
    }
}

export abstract class BillingTrendResult
{
    identity: BillingLineItemExtended;
    trend: BillingTrendPoints;
    forecastedTrend: BillingTrendPoints;
    numAccounts: number;

    sourceCost?: BillingTrendPoints;
    contractedCost?: BillingTrendPoints;
    margin?: BillingTrendPoints;
    profitMargin?: BillingTrendPoints;
    retailCost?: BillingTrendPoints;
    billingSource?: string;
    forecastedSource?: BillingTrendPoints;
    forecastedContracted?: BillingTrendPoints;
    forecastedMargin?: BillingTrendPoints;
    forecastedProfitMargin?: BillingTrendPoints;

    denominatorPoints?: BillingTrendPoints;
    resourceServiceAccountId?: string;

    abstract convertToAggregation(): BillingAggregationResult;

    abstract alignTrendWithGranularityRanges(granularityRanges: RangeSelectionExtended[],
                                             startTime: number,
                                             endTime: number,
                                             forecastEndTime: number,
                                             billingSource?: string,
                                             forecastStartTime?: number): BillingTrendResult;

    abstract convertToYearly(): BillingTrendResult;

    abstract convertCurrency(currencyConverter: CurrencyConverter,
                             currency: Models.Currency): BillingTrendResultExtended;

    abstract convertTrendPointsToYearly(points: BillingTrendPoint[]): BillingTrendPoint[];
}

export class BillingTrendResultExtended extends BillingTrendResult
{
    constructor(public readonly identity: BillingLineItemExtended,
                public readonly trend: BillingTrendPoints,
                public readonly forecastedTrend: BillingTrendPoints,
                public numAccounts: number)
    {
        super();
    }

    public static newInstance(filterOptions: FilterOptionsExtended,
                              model: BillingTrendInterface,
                              groupBys: BillingGroupBy[],
                              isBillingRange: boolean,
                              quantityRates?: boolean,
                              isActualSource?: boolean): BillingTrendResultExtended
    {
        const identity                   = BillingLineItemExtended.newInstance(filterOptions, model.identity, groupBys);
        const trend: BillingTrendPoint[] = [];
        const hasDescriptions            = Array.isArray(model?.descriptions) && model.descriptions.length > 0;
        for (let i = 0; i < model.values.length; i++)
        {
            let timestamp        = model.timestamps[i];
            let billingTimestamp = timestamp;
            if (isBillingRange)
            {
                timestamp = new Date(timestamp.valueOf());
                timestamp.setUTCDate(1);
            }

            if (quantityRates)
            {
                if (isActualSource)
                {
                    trend.push(new BillingTrendPoint(model.values[i], timestamp, billingTimestamp, model?.descriptions?.[i], undefined, undefined, undefined, undefined, model.rates[i]));
                }
                else
                {
                    trend.push(new BillingTrendPoint(model.values[i],
                                                     timestamp,
                                                     billingTimestamp,
                                                     model?.descriptions?.[i],
                                                     model.rates[i],
                                                     model.usages[i],
                                                     undefined,
                                                     undefined));
                }
            }
            else if (hasDescriptions)
            {
                const descriptions = model.descriptions[i];
                trend.push(new BillingTrendPoint(model.values[i], timestamp, billingTimestamp, descriptions));
            }
            else
            {
                trend.push(new BillingTrendPoint(model.values[i], timestamp, billingTimestamp));
            }
        }

        return new BillingTrendResultExtended(identity, new BillingTrendPoints(trend), null, 1);
    }

    public convertCurrency(converter: CurrencyConverter,
                           targetCurrency: Models.Currency): BillingTrendResultExtended
    {
        if (this.identity.model.currency == targetCurrency) return this;
        const valueConverter  = converter.getConverter(this.identity.tenantId, this.identity.model.currency);
        const trend           = this.trend.points.map(point => new BillingTrendPoint(valueConverter(point.value, point.timestamp.valueOf()), point.timestamp, point.billingTimestamp));
        const forecastedTrend = this.forecastedTrend?.points.map(point => new BillingTrendPoint(valueConverter(point.value, point.timestamp.valueOf()), point.timestamp, point.billingTimestamp));
        return new BillingTrendResultExtended(this.identity, new BillingTrendPoints(trend), forecastedTrend && new BillingTrendPoints(forecastedTrend), this.numAccounts);
    }

    public convertToYearly(): BillingTrendResult
    {
        const trend           = this.convertTrendPointsToYearly(this.trend.points);
        const forecastedTrend = this.forecastedTrend && new BillingTrendPoints(this.convertTrendPointsToYearly(this.forecastedTrend.points));
        return new BillingTrendResultExtended(this.identity, new BillingTrendPoints(trend), forecastedTrend, this.numAccounts);
    }

    public convertTrendPointsToYearly(points: BillingTrendPoint[]): BillingTrendPoint[]
    {
        const trend: BillingTrendPoint[]    = [];
        let currentPoint: BillingTrendPoint = null;
        for (let point of points)
        {
            const mom   = MomentHelper.parseAsUTC(point.timestamp);
            const start = mom.clone()
                             .startOf("year")
                             .valueOf();
            if (!currentPoint || currentPoint.timestamp.valueOf() != start)
            {
                if (currentPoint) trend.push(currentPoint);
                let timestamp = new Date(start);
                currentPoint  = new BillingTrendPoint(0, timestamp, timestamp);
            }

            currentPoint.add(point);
        }
        if (currentPoint) trend.push(currentPoint);

        return trend;
    }

    public convertToAggregation(): BillingAggregationResultExtended
    {
        return new BillingAggregationResultExtended(this.identity, this.trend.total, this.numAccounts);
    }

    public alignTrendWithGranularityRanges(ranges: RangeSelectionExtended[],
                                           start: number,
                                           end: number,
                                           forecastEnd?: number,
                                           billingSource?: string,
                                           forecastStart?: number): BillingTrendResult
    {
        const forecastedTrend = this.forecastedTrend?.from(forecastStart)
                                    .through(forecastEnd)
                                    .alignTrendWithGranularityRanges(ranges, end, forecastEnd);
        return new BillingTrendResultExtended(this.identity, this.trend.alignTrendWithGranularityRanges(ranges, start, end), forecastedTrend, this.numAccounts);
    }
}

// Extended margin trend result
export class BillingMarginResultExtended extends BillingTrendResultExtended
{
    constructor(identity: BillingLineItemExtended,
                public readonly sourceCost: BillingTrendPoints,
                public readonly contractedCost: BillingTrendPoints,
                public readonly margin: BillingTrendPoints,
                public readonly profitMargin: BillingTrendPoints,
                numAccounts: number,
                billingSource: string,
                public readonly forecastedSource?: BillingTrendPoints,
                public readonly forecastedContracted?: BillingTrendPoints,
                public readonly forecastedMargin?: BillingTrendPoints,
                public readonly forecastedProfitMargin?: BillingTrendPoints,
                public readonly retailCost?: BillingTrendPoints,
                public readonly forecastedRetailCost?: BillingTrendPoints)
    {
        let values: BillingTrendPoints;
        switch (billingSource)
        {
            case BillingSourceType.MarginValue:
            case BillingSourceType.PartnerMarginValue:
                values = margin;
                break;

            case BillingSourceType.ProfitMargin:
            case BillingSourceType.PartnerProfitMargin:
                values = profitMargin;
                break;
        }
        super(identity, values, null, numAccounts);
    }

    public convertToYearly(billingSource?: string): BillingTrendResult
    {
        const sourceCost     = new BillingTrendPoints(this.convertTrendPointsToYearly(this.sourceCost.points));
        const contractedCost = new BillingTrendPoints(this.convertTrendPointsToYearly(this.contractedCost.points));
        const margin         = new BillingTrendPoints(this.convertTrendPointsToYearly(this.margin.points));
        const profitMargin   = new BillingTrendPoints(this.convertTrendPointsToYearly(this.profitMargin.points));
        const retailCost     = this.retailCost && new BillingTrendPoints(this.convertTrendPointsToYearly(this.retailCost.points));

        return new BillingMarginResultExtended(this.identity, sourceCost, contractedCost, margin, profitMargin, this.numAccounts, billingSource,
                                               undefined, undefined, undefined, undefined, retailCost);
    }

    public alignTrendWithGranularityRanges(ranges: RangeSelectionExtended[],
                                           start: number,
                                           end: number,
                                           forecastEnd?: number,
                                           billingSource?: string,
                                           forecastStart?: number): BillingTrendResult
    {
        const sourceCost                 = this.sourceCost.alignTrendWithGranularityRanges(ranges, start, end);
        const contractedCost             = this.contractedCost.alignTrendWithGranularityRanges(ranges, start, end);
        const margin                     = this.margin.alignTrendWithGranularityRanges(ranges, start, end);
        const profitMargin               = this.profitMargin.alignTrendWithGranularityRanges(ranges, start, end);
        const forecastedSourceCost       = this.forecastedSource?.from(forecastStart)
                                               .through(forecastEnd)
                                               .alignTrendWithGranularityRanges(ranges, end, forecastEnd);
        const forecastedContractedCost   = this.forecastedContracted?.from(forecastStart)
                                               .through(forecastEnd)
                                               .alignTrendWithGranularityRanges(ranges, end, forecastEnd);
        const forecastedMarginCost       = this.forecastedMargin?.from(forecastStart)
                                               .through(forecastEnd)
                                               .alignTrendWithGranularityRanges(ranges, end, forecastEnd);
        const forecastedProfitMarginCost = this.forecastedProfitMargin?.from(forecastStart)
                                               .through(forecastEnd)
                                               .alignTrendWithGranularityRanges(ranges, end, forecastEnd);
        const retailCost                 = this.retailCost?.alignTrendWithGranularityRanges(ranges, start, end);
        const forecastedRetailCost       = this.forecastedRetailCost?.from(forecastStart)
                                               .through(forecastEnd)
                                               .alignTrendWithGranularityRanges(ranges, end, forecastEnd);

        return new BillingMarginResultExtended(this.identity,
                                               sourceCost,
                                               contractedCost,
                                               margin,
                                               profitMargin,
                                               this.numAccounts,
                                               billingSource,
                                               forecastedSourceCost,
                                               forecastedContractedCost,
                                               forecastedMarginCost,
                                               forecastedProfitMarginCost,
                                               retailCost,
                                               forecastedRetailCost);
    }
}

export class BillingK8sResultExtended extends BillingTrendResultExtended
{
    constructor(identity: BillingLineItemExtended,
                numAccounts: number,
                values: BillingTrendPoints,
                public forecastedTrend: BillingTrendPoints,
                public denominatorPoints?: BillingTrendPoints,
                public scopedCostTrend?: BillingTrendPoints,
                public readonly resourceServiceAccountId?: string)
    {
        if (scopedCostTrend)
        {
            values              = scopedCostTrend;
            let totalCloudSpend = denominatorPoints?.total;
            if (totalCloudSpend == null)
            {
                totalCloudSpend = 0;
                for (const point of scopedCostTrend.points)
                {
                    totalCloudSpend += Number(point.billingCost) || 0;
                }
            }

            scopedCostTrend.totalCloudSpend = totalCloudSpend;
            scopedCostTrend.totalScopedCost = totalCloudSpend != 0 ? (scopedCostTrend.total / totalCloudSpend) : 0;
        }
        super(identity, values, null, numAccounts);
    }

    public convertToYearly(): BillingTrendResult
    {
        const values               = new BillingTrendPoints(this.convertTrendPointsToYearly(this.trend.points));
        const billingTrendPoint    = new BillingTrendPoints(this.convertTrendPointsToYearly(this.contractedCost.points));
        const forecastedTrendPoint = this.forecastedTrend && new BillingTrendPoints(this.convertTrendPointsToYearly(this.forecastedTrend.points));
        const scopedTrendPoint     = new BillingTrendPoints(this.convertTrendPointsToYearly(this.margin.points));

        return new BillingK8sResultExtended(this.identity, this.numAccounts, values, forecastedTrendPoint, billingTrendPoint, scopedTrendPoint);
    }

    public alignTrendWithGranularityRanges(ranges: RangeSelectionExtended[],
                                           start: number,
                                           end: number,
                                           forecastEnd?: number,
                                           billingSource?: string,
                                           forecastStart?: number): BillingTrendResult
    {
        const values          = this.trend.alignTrendWithGranularityRanges(ranges, start, end);
        const billingTrend    = this.denominatorPoints.alignTrendWithGranularityRanges(ranges, start, end);
        const forecastedTrend = this.forecastedTrend?.from(forecastStart)
                                    .through(forecastEnd)
                                    .alignTrendWithGranularityRanges(ranges, end, forecastEnd);
        const scopedTrend     = this.scopedCostTrend?.alignTrendWithGranularityRanges(ranges, start, end);

        return new BillingK8sResultExtended(this.identity,
                                            this.numAccounts,
                                            values,
                                            forecastedTrend,
                                            billingTrend,
                                            scopedTrend,
                                            this.resourceServiceAccountId);
    }
}

export class BillingTrendPoint
{
    constructor(public value: number,
                public readonly timestamp: Date,
                public readonly billingTimestamp: Date,
                public readonly descriptions?: string,
                public rate?: number,
                public usage?: number,
                public scopedCost?: number,
                public billingCost?: number,
                public billedRate?: number)
    {}

    public add(other: BillingTrendPoint): BillingTrendPoint
    {
        if (other?.descriptions)
        {
            return new BillingTrendPoint(this.value + other.value, this.timestamp, this.billingTimestamp, this.descriptions + other.descriptions);
        }

        return new BillingTrendPoint(this.value + other.value, this.timestamp, this.billingTimestamp, null, null, null, this.scopedCost, this.billingCost);
    }
}

export class BillingTrendPoints
{
    public total: number           = 0;
    public totalScopedCost: number = 0;
    public totalCloudSpend: number = 0;

    constructor(public readonly points: BillingTrendPoint[])
    {
        for (let point of points)
        {
            this.total += point.value;
        }
    }

    public static actualValues(computedValues: BillingTrendPoints,
                               actualValues: BillingTrendPoints,
                               forecastedValues: BillingTrendPoints,
                               useActualValuesBeforeForecast: boolean): BillingTrendPoints
    {
        return useActualValuesBeforeForecast && actualValues.points.length
            ? actualValues
            : computedValues.subtract(forecastedValues);
    }

    public through(end?: number): BillingTrendPoints
    {
        return end == null
            ? this
            : new BillingTrendPoints(this.points.filter(point => point.timestamp.valueOf() <= end));
    }

    public from(start?: number): BillingTrendPoints
    {
        return start == null
            ? this
            : new BillingTrendPoints(this.points.filter(point => point.timestamp.valueOf() >= start));
    }

    public alignTrendWithGranularityRanges(granularityRanges: RangeSelectionExtended[],
                                           start: number,
                                           end: number): BillingTrendPoints
    {
        const existingTimestamps          = new Set(this.points.map(point => point.timestamp.valueOf()));
        const points: BillingTrendPoint[] = [];

        for (let range of granularityRanges)
        {
            const timeStampDate = range.getMinAsDate();
            const timestamp     = timeStampDate.valueOf();
            if (timestamp >= start)
            {
                if (timestamp >= end) break;
                if (existingTimestamps.has(timestamp)) continue;

                const trendPoint = new BillingTrendPoint(0, timeStampDate, timeStampDate, null, null, null, 0, 0);
                points.push(trendPoint);
            }
        }
        const emptyPoints = new BillingTrendPoints(points);
        return this.merge(emptyPoints);
    }

    /**
     * Merge two trends, generating a new trend with the sum of the values.
     * @param other The trend to merge with this one
     */
    public merge(other: BillingTrendPoints): BillingTrendPoints
    {
        let i                             = 0;
        let j                             = 0;
        const result: BillingTrendPoint[] = [];
        while (i < this.points.length || j < other.points.length)
        {
            const pointA = this.points[i];
            const pointB = other.points[j];
            if (pointA && pointB)
            {
                if (pointA.timestamp < pointB.timestamp)
                {
                    result.push(pointA);
                    i++;
                }
                else if (pointA.timestamp > pointB.timestamp)
                {
                    result.push(pointB);
                    j++;
                }
                else
                {
                    result.push(pointA.add(pointB));
                    i++;
                    j++;
                }
            }
            else if (pointA)
            {
                result.push(pointA);
                i++;
            }
            else
            {
                result.push(pointB);
                j++;
            }
        }
        return new BillingTrendPoints(result);
    }

    /**
     * Subtract values in other from the values in this
     * @param other The trend to subtract from this one
     * @param includeZeroValue
     */
    public subtract(other: BillingTrendPoints,
                    includeZeroValue?: boolean): BillingTrendPoints
    {
        const result: BillingTrendPoint[] = [];

        let otherIdx       = 0;
        let otherPoint     = other.points[otherIdx++];
        let otherPointTime = otherPoint?.timestamp.valueOf();
        for (const point of this.points)
        {
            let value      = point.value;
            let billedRate = point?.billedRate;

            if (otherPointTime == point.timestamp.valueOf())
            {
                value -= otherPoint.value;
                // Preserve billedRate from other point if available
                billedRate = billedRate || otherPoint?.billedRate;

                otherPoint     = other.points[otherIdx++];
                otherPointTime = otherPoint?.timestamp.valueOf();
            }

            if (value || includeZeroValue) result.push(new BillingTrendPoint(value, point.timestamp, point.billingTimestamp, "", point?.rate, point?.usage, undefined, undefined, billedRate));
        }

        return new BillingTrendPoints(result);
    }

    public divide(other: BillingTrendPoints,
                  includeZeroValue?: boolean): BillingTrendPoints
    {
        const result: BillingTrendPoint[] = [];

        let otherIdx       = 0;
        let otherPoint     = other.points[otherIdx++];
        let otherPointTime = otherPoint?.timestamp.valueOf();

        for (const point of this.points)
        {
            let value: number = point.value;
            let billedRate    = point?.billedRate;

            if (otherPointTime === point.timestamp.valueOf())
            {
                const margin = point.value - otherPoint.value;
                if (margin != 0 && point.value != 0)
                {
                    value = (margin / point.value) * 100;
                }
                else
                {
                    value = 0;
                }

                // Preserve billedRate from other point if available
                billedRate = billedRate || otherPoint?.billedRate;

                otherPoint     = other.points[otherIdx++];
                otherPointTime = otherPoint?.timestamp.valueOf();
            }

            if (value || includeZeroValue) result.push(new BillingTrendPoint(value, point.timestamp, point.billingTimestamp, "", point?.rate, point?.usage, undefined, undefined, billedRate));
        }

        return new BillingTrendPoints(result);
    }

    public extractPointsAfter(timestamp: Date): BillingTrendPoints
    {
        let i = this.points.findIndex((point) => point.timestamp >= timestamp);
        return new BillingTrendPoints(i == -1 ? [] : this.points.splice(i));
    }
}

export class BillingLineItemExtended
{
    public static newInstance(filterOptions: FilterOptionsExtended,
                              model: Models.BillingLineItemIdentity,
                              groupBys: BillingGroupBy[]): BillingLineItemExtended
    {
        const serviceAccountInfo = filterOptions?.resolveAccount(model.service_account_id);
        const tenantInfo         = filterOptions?.resolveTenant(serviceAccountInfo?.tenantId);
        return new BillingLineItemExtended(model, tenantInfo, serviceAccountInfo, groupBys);
    }

    constructor(public readonly model: Models.BillingLineItemIdentity,
                public readonly tenant: TenantInfo,
                public readonly cloudAccount: ServiceAccountInfo,
                public readonly groupBys: BillingGroupBy[])
    {}

    public get tenantId(): string
    {
        return this.tenant?.tenantId;
    }

    public get tenantName(): string
    {
        return this.tenant?.name;
    }

    public get tenantTags(): Models.KeyValuePair[]
    {
        return this.tenant?.tags;
    }

    public get tenantTagValue(): string
    {
        const groupBy = this.groupBys.find(g => g.selector === GroupBySelector.tenant_tags);
        return this.tenantTags?.find(t => t.key === groupBy?.key)?.value || "None";
    }

    public get custom(): Models.KeyValuePair[]
    {
        return this.model?.custom;
    }

    public get cloudAccountName(): string
    {
        return this.cloudAccount?.name;
    }

    public get hierarchy(): Models.KeyValuePair[]
    {
        return this.model.hierarchy;
    }

    public get cloud(): Models.CloudServiceName
    {
        return this.cloudAccount?.cloud;
    }

    public get cloudAccountTagValue(): string
    {
        const groupBy = this.groupBys?.find(g => g.selector === GroupBySelector.service_account_tags);
        return this.cloudAccount?.tags?.find(t => t.key === groupBy?.key)?.value || "None";
    }
}

abstract class BillingGroupByHandler
{
    constructor(protected m_groupBy: BillingGroupBy) {}

    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return null;
    }

    public abstract getKey(identity: BillingLineItemExtended): string;
}

type groupBySubset1 = Exclude<SupportedBillingGroupBy, GroupBySelector.tenant_id>;
type groupBySubset2 = Exclude<groupBySubset1, GroupBySelector.service_name | GroupBySelector.saas_providers>;
type groupBySubset3 = Exclude<groupBySubset2, GroupBySelector.service_account_id>;
type groupBySubset4 = Exclude<groupBySubset3, GroupBySelector.account_type | GroupBySelector.tag_status | GroupBySelector.tag_keys | GroupBySelector.dimension | GroupBySelector.cloud_account_hierarchy | GroupBySelector.compartment_name | GroupBySelector.compartment_path | GroupBySelector.extras | GroupBySelector.charge_description | GroupBySelector.consumed_quantity_and_unit_price | GroupBySelector.pods | GroupBySelector.workload_type | GroupBySelector.k8Tags | GroupBySelector.tags>;

export type GroupByCategoriesWithMapping = groupBySubset4;

abstract class BillingGroupBySimpleHandler<T extends GroupByCategoriesWithMapping> extends BillingGroupByHandler
{
    public abstract readonly billingGroupBy: T;

    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: UtilsService.parseEnumValue<Models.BillingGroupBySelector>(GroupBySelector, this.billingGroupBy)
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        const raw = (identity.model as Models.BillingLineItemIdentity)?.[this.billingGroupBy];

        if (typeof raw === "string") return raw;

        // For non-string fields (e.g. KeyValuePair[]), return null so callers bucket them safely.
        return null;
    }
}

export class BillingGroupByHandlerMulti
{
    private readonly m_handlers: BillingGroupByHandler[] = [];

    constructor(aggTypes: BillingGroupBy[])
    {
        for (const aggType of aggTypes || [])
        {
            if (aggType.selector === GroupBySelector.workload_type)
            {
                const workloadTypes = UtilsService.getEnumValues<KubernetesWorkloadType>(KubernetesWorkloadType);

                // Create one handler for each workload type
                for (const workloadType of workloadTypes)
                {
                    const modifiedAggType = Models.BillingGroupByDetails.newInstance({
                                                                                         selector: Models.BillingGroupBySelector.extras,
                                                                                         key     : workloadType
                                                                                     });

                    const handlerType = handlers[aggType.selector];
                    if (handlerType) this.m_handlers.push(new handlerType(modifiedAggType));
                }
            }
            else
            {
                const handlerType = handlers[aggType.selector];
                if (handlerType) this.m_handlers.push(new handlerType(aggType));
            }
        }
    }

    private shouldFilterNullKey(identity: BillingLineItemExtended): boolean
    {
        const isGroupByCloudProvider = identity.groupBys?.some(g => g.selector === GroupBySelector.service_name);
        if (!isGroupByCloudProvider) return false;
        const key = this.getKey(identity);
        return !key;
    }

    public groupResults(values: BillingAggregationResultExtended[]): BillingAggregationResultExtended[]
    {
        const buckets = UtilsService.createBuckets(
            values.filter(val => !this.shouldFilterNullKey(val.identity)),
            val => this.getKey(val.identity)
        );
        return Object.entries(buckets)
                     .map(([key, values]) =>
                          {
                              const total       = UtilsService.sum(values.map(v => v.value));
                              const accounts    = new Set<string>(values.map(v => v.serviceAccountId));
                              const lineItemExt = this.getIdentity(key, values[0].identity);
                              return new BillingAggregationResultExtended(lineItemExt, total, accounts.size);
                          });
    }

    public groupTrend(values: BillingTrendResult[],
                      granularity: Models.TrendGranularity,
                      currGranularityActualValues: BillingTrendResult[],
                      isBillingSourceMargin: boolean,
                      billingSource: string,
                      platformAsKubernetes?: boolean,
                      useActualValuesBeforeForecast?: boolean,
                      forecastSplitTime?: number,
                      fiscalStartMonth: number = 0): BillingTrendResult[]
    {
        const expandedValues = this.expandWorkloadType(values);
        const buckets        = UtilsService.createBuckets(
            expandedValues.filter(val => !this.shouldFilterNullKey(val.identity)),
            val => this.getKey(val.identity));

        const currGranularityBuckets = currGranularityActualValues &&
                                       UtilsService.createBuckets(currGranularityActualValues.filter(val => !this.shouldFilterNullKey(val.identity)),
                                                                  val => this.getKey(val.identity));

        const currGranularity               = currGranularityActualValues && new Date(normalizeForecastPeriodStart(forecastSplitTime,
                                                                                                                  granularity,
                                                                                                                  fiscalStartMonth));
        const grouped: BillingTrendResult[] = [];
        for (const key in buckets)
        {
            const series   = buckets[key];
            const accounts = new Set<string>();

            let valuesActual                        = new BillingTrendPoints([]);
            let denominatorInfo: K8ScopedTrendPoint = {
                id    : "",
                points: new BillingTrendPoints([])
            };
            const resourceServiceAccountIds         = new Set<string>();
            let sourceCost                          = new BillingTrendPoints([]);
            let contractedCost                      = new BillingTrendPoints([]);
            let retailCost                          = new BillingTrendPoints([]);

            for (const item of series)
            {
                accounts.add(item.identity.model.service_account_id);
                valuesActual = valuesActual.merge(item.trend);
                if (item.resourceServiceAccountId) resourceServiceAccountIds.add(item.resourceServiceAccountId);
                if (item.denominatorPoints)
                {
                    denominatorInfo.points = denominatorInfo.points.merge(item.denominatorPoints);
                }

                if (isBillingSourceMargin)
                {
                    sourceCost     = sourceCost.merge(item.sourceCost);
                    contractedCost = contractedCost.merge(item.contractedCost);
                    if (item.retailCost) retailCost = retailCost.merge(item.retailCost);
                }
            }

            const lineItemExt = this.getIdentity(key, series[0].identity);

            let valuesForecasted: BillingTrendPoints;
            let forecastedSource: BillingTrendPoints;
            let forecastedContracted: BillingTrendPoints;

            if (currGranularityBuckets && Object.keys(currGranularityBuckets).length)
            {
                let currValuesActual = new BillingTrendPoints([]);
                let sourceActual     = new BillingTrendPoints([]);
                let contractedActual = new BillingTrendPoints([]);

                for (const currS of currGranularityBuckets[key] || [])
                {
                    currValuesActual = currValuesActual.merge(currS.trend);
                    if (isBillingSourceMargin)
                    {
                        sourceActual     = sourceActual.merge(currS.sourceCost);
                        contractedActual = contractedActual.merge(currS.contractedCost);
                    }
                }

                if (isBillingSourceMargin)
                {
                    if (sourceActual.points.length)
                    {
                        forecastedSource = sourceCost.subtract(sourceActual)
                                                     .extractPointsAfter(currGranularity);
                        sourceCost       = BillingTrendPoints.actualValues(sourceCost, sourceActual, forecastedSource, useActualValuesBeforeForecast);
                    }
                    else
                    {
                        forecastedSource = sourceCost.extractPointsAfter(currGranularity);
                        sourceCost       = sourceCost.subtract(forecastedSource);
                    }

                    if (contractedActual.points.length)
                    {
                        forecastedContracted = contractedCost.subtract(contractedActual)
                                                             .extractPointsAfter(currGranularity);
                        contractedCost       = BillingTrendPoints.actualValues(contractedCost, contractedActual, forecastedContracted, useActualValuesBeforeForecast);
                    }
                    else
                    {
                        forecastedContracted = contractedCost.extractPointsAfter(currGranularity);
                        contractedCost       = contractedCost.subtract(forecastedContracted);
                    }
                }

                if (currValuesActual.points.length)
                {
                    const allValuesMinusCurrActual = valuesActual.subtract(currValuesActual);
                    valuesForecasted               = allValuesMinusCurrActual.extractPointsAfter(currGranularity);
                    valuesActual                   = BillingTrendPoints.actualValues(valuesActual, currValuesActual, valuesForecasted, useActualValuesBeforeForecast);
                }
                else
                {
                    valuesForecasted = valuesActual.extractPointsAfter(currGranularity);
                    valuesActual     = valuesActual.subtract(valuesForecasted);
                }
            }

            if (isBillingSourceMargin)
            {
                const margin                 = contractedCost.subtract(sourceCost, true);
                const profitMargin           = contractedCost.divide(sourceCost, true);
                const forecastedMargin       = forecastedContracted?.subtract(forecastedSource, true);
                const forecastedProfitMargin = forecastedContracted?.divide(forecastedSource, true);

                grouped.push(new BillingMarginResultExtended(lineItemExt,
                                                             sourceCost,
                                                             contractedCost,
                                                             margin,
                                                             profitMargin,
                                                             accounts.size,
                                                             billingSource,
                                                             forecastedSource,
                                                             forecastedContracted,
                                                             forecastedMargin,
                                                             forecastedProfitMargin,
                                                             retailCost));
            }
            else if (platformAsKubernetes)
            {
                const denominatorCostsPerTimestamp: Lookup<number, number> = {};
                for (const point of denominatorInfo.points.points)
                {
                    const timestamp                         = point.timestamp.valueOf();
                    denominatorCostsPerTimestamp[timestamp] = (denominatorCostsPerTimestamp[timestamp] ?? 0) + point.value;
                }

                const k8TrendPoints: BillingTrendPoint[] = valuesActual.points.map(point =>
                                                                                   {
                                                                                       const date        = point.timestamp.valueOf();
                                                                                       const billingCost = denominatorCostsPerTimestamp[date] || 0;
                                                                                       const scopedCost  = billingCost !== 0 ? point.value / billingCost : 0;
                                                                                       return new BillingTrendPoint(point.value,
                                                                                                                    point.timestamp,
                                                                                                                    point.billingTimestamp,
                                                                                                                    point.descriptions,
                                                                                                                    point.rate,
                                                                                                                    point.usage,
                                                                                                                    scopedCost,
                                                                                                                    billingCost || 0,
                                                                                                                    point?.billedRate);
                                                                                   });

                const scopedTrend              = new BillingTrendPoints(k8TrendPoints);
                const resourceServiceAccountId = resourceServiceAccountIds.size === 1 ? Array.from(resourceServiceAccountIds)[0] : undefined;
                grouped.push(new BillingK8sResultExtended(lineItemExt,
                                                          accounts.size,
                                                          valuesActual,
                                                          valuesForecasted,
                                                          denominatorInfo.points,
                                                          scopedTrend,
                                                          resourceServiceAccountId));
            }
            else
            {
                grouped.push(new BillingTrendResultExtended(lineItemExt, valuesActual, valuesForecasted, accounts.size));
            }
        }

        return grouped;
    }

    private expandWorkloadType(values: BillingTrendResult[]): BillingTrendResult[]
    {
        const expanded: BillingTrendResult[] = [];

        for (const value of values)
        {
            const k8BillingValue   = value as BillingK8sResultExtended;
            const hasWorkloadGroup = value?.identity?.groupBys?.find(g => g.selector === GroupBySelector.workload_type);
            const extras           = value.identity.model?.extras;

            if (hasWorkloadGroup && extras?.length)
            {
                const filteredExtras = K8sWorkloadTypeBillingGroupByHandler.filterWorkloadsByPrecedence(extras);

                for (const workloadType of filteredExtras)
                {
                    const clonedModel = {
                        ...value.identity.model,
                        extras: [workloadType]
                    };

                    const newIdentity = new BillingLineItemExtended(Models.BillingLineItemIdentity.newInstance(clonedModel),
                                                                    value.identity.tenant,
                                                                    value.identity.cloudAccount,
                                                                    value.identity.groupBys);

                    expanded.push(new BillingK8sResultExtended(newIdentity,
                                                               value.numAccounts,
                                                               value.trend,
                                                               null,
                                                               value.denominatorPoints,
                                                               k8BillingValue.scopedCostTrend,
                                                               k8BillingValue.resourceServiceAccountId));
                }
            }
            else
            {
                expanded.push(value);
            }
        }

        return expanded;
    }

    public computeMarginTrendValues(contractedCost: BillingTrendResult[],
                                    sourceCost: BillingTrendResult[],
                                    billingSource: string,
                                    retailCostResults?: BillingTrendResult[]): BillingMarginResultExtended[]
    {
        const contractedCostBuckets = UtilsService.createBuckets(contractedCost?.filter(val => !this.shouldFilterNullKey(val.identity)), val => this.getKey(val.identity));
        const sourceCostBuckets     = UtilsService.createBuckets(sourceCost?.filter(val => !this.shouldFilterNullKey(val.identity)), val => this.getKey(val.identity));
        const retailCostBuckets     = UtilsService.createBuckets(retailCostResults?.filter(val => !this.shouldFilterNullKey(val.identity)) || [], val => this.getKey(val.identity));

        const result: BillingMarginResultExtended[] = [];

        const allKeys = new Set<string>([
                                            ...Object.keys(contractedCostBuckets),
                                            ...Object.keys(sourceCostBuckets)
                                        ]);

        for (const key of allKeys)
        {
            const contractedCostList = contractedCostBuckets[key] || [];
            const sourceCostList     = sourceCostBuckets[key] || [];
            const retailCostList     = retailCostBuckets[key] || [];

            let combinedContractedCost = new BillingTrendPoints([]);
            let combinedSourceCost     = new BillingTrendPoints([]);
            let combinedRetailCost     = new BillingTrendPoints([]);
            const accounts             = new Set<string>();

            for (const item of contractedCostList)
            {
                accounts.add(item.identity.model.service_account_id);
                combinedContractedCost = combinedContractedCost.merge(item.trend);
            }

            for (const item of sourceCostList)
            {
                accounts.add(item.identity.model.service_account_id);
                combinedSourceCost = combinedSourceCost.merge(item.trend);
            }

            for (const item of retailCostList)
            {
                combinedRetailCost = combinedRetailCost.merge(item.trend);
            }

            const identitySource     = contractedCostList[0]?.identity || sourceCostList[0]?.identity;
            const lineItem           = this.getIdentity(key, identitySource);
            const marginValues       = combinedContractedCost.subtract(combinedSourceCost, true);
            const profitMarginValues = combinedContractedCost.divide(combinedSourceCost, true);

            result.push(new BillingMarginResultExtended(lineItem, combinedSourceCost, combinedContractedCost, marginValues, profitMarginValues, accounts.size, billingSource,
                                                         undefined, undefined, undefined, undefined,
                                                         retailCostResults ? combinedRetailCost : undefined));
        }

        return result;
    }

    public getGroupByIdentifierValue(value: BillingTrendResult,
                                     groupBy: BillingGroupBy[]): string
    {
        let identifier     = "";
        const groupByValue = groupBy?.[0]?.selector;

        switch (groupByValue)
        {
            case GroupBySelector.service_name:
                identifier = CloudProvidersService.isAzure(value.identity.cloud) ? Models.CloudServiceName.Azure : value.identity.cloud;
                break;

            case GroupBySelector.service_account_id:
                identifier = value.identity.cloudAccountName;
                break;

            case GroupBySelector.tenant_id:
                identifier = value.identity.tenantName;
                break;

            case GroupBySelector.product_category:
                identifier = value.identity.model.product_category || GroupByIdentifierFriendlyName.Uncategorized;
                break;

            case GroupBySelector.resource_category:
                identifier = value.identity.model.resource_category || GroupByIdentifierFriendlyName.Uncategorized;
                break;

            case GroupBySelector.compartment_name:
                identifier = value.identity.model.compartment_name || GroupByIdentifierFriendlyName.Null;
                break;

            case GroupBySelector.compartment_path:
                identifier = value.identity.model.compartment_path || GroupByIdentifierFriendlyName.Null;
                break;

            case GroupBySelector.location:
                identifier = value.identity.model.location || GroupByIdentifierFriendlyName.Null;
                break;

            case GroupBySelector.line_item_type:
                identifier = value.identity.model.line_item_type || GroupByIdentifierFriendlyName.Uncategorized;
                break;

            case GroupBySelector.utilization_type:
                identifier = value.identity.model.utilization_type || GroupByIdentifierFriendlyName.Uncategorized;
                break;

            case GroupBySelector.resource_group:
                identifier = value.identity.model.resource_group || GroupByIdentifierFriendlyName.NoResourceGroup;
                break;

            case GroupBySelector.resource_type:
                identifier = value.identity.model.resource_type || GroupByIdentifierFriendlyName.Null;
                break;

            case GroupBySelector.tag_keys:
                identifier = value.identity.model.tags?.[0]?.key || GroupByIdentifierFriendlyName.Null;
                break;

            case GroupBySelector.workload_type:
                identifier = value.identity.model.extras?.[0]?.key || GroupByIdentifierFriendlyName.Null;
                break;

            case GroupBySelector.extras:
            case GroupBySelector.pods:
                identifier = value.identity.model.extras?.[0]?.value || GroupByIdentifierFriendlyName.Null;
                break;
        }
        return identifier;
    }

    private getNormalizedServiceAccountId(accountToDetails: Lookup<Models.ServiceAccountDetails>,
                                          serviceAccountId?: string): string | undefined
    {
        if (!serviceAccountId) return undefined;
        return accountToDetails[serviceAccountId]?.resource_service_account_id || serviceAccountId;
    }

    private getK8DenominatorKey(identity: BillingLineItemExtended,
                                isGroupBySA: boolean,
                                accountToDetails: Lookup<Models.ServiceAccountDetails>,
                                k8Handler: K8BillingGroupByHandlerMulti,
                                denominatorSuffixHandler: K8BillingGroupByHandlerMulti | null): string
    {
        if (!isGroupBySA) return k8Handler.getDenominatorKey(identity);

        const normalizedServiceAccountId = this.getNormalizedServiceAccountId(accountToDetails, identity?.model?.service_account_id) || "__no_sa__";
        const suffix                     = denominatorSuffixHandler?.getDenominatorKey(identity) || "";
        if (!suffix || suffix === "__all__") return normalizedServiceAccountId;
        return `${normalizedServiceAccountId}/${suffix}`;
    }

    private async getK8ScopedCostComputationContext(k8Values: Array<{ identity: BillingLineItemExtended }>,
                                                    billingValues: Array<{ identity: BillingLineItemExtended }>,
                                                    fetcher: ServiceAccountFetcher,
                                                    groupBy: BillingGroupBy[],
                                                    domain: AppDomainContext): Promise<BillingK8ScopedCostComputationContextBase>
    {
        const isGroupBySA = groupBy?.some(g => g.selector === GroupBySelector.service_account_id);
        const k8Handler   = new K8BillingGroupByHandlerMulti(groupBy);

        let accountToDetails: Lookup<Models.ServiceAccountDetails> = {};
        if (isGroupBySA)
        {
            fetcher ||= domain?.accountGovernanceList.cachedAutoBatcher<AccountGovernanceListService>();
            const serviceAccountIds = Array.from(new Set([
                                                             ...k8Values.map(t => t?.identity?.model?.service_account_id),
                                                             ...billingValues.map(t => t?.identity?.model?.service_account_id)
                                                         ].filter((id): id is string => !!id)));
            accountToDetails        = await fetcher?.getBatchAsLookup(serviceAccountIds) || {};
        }

        const nonServiceAccountGroupBy = (groupBy || []).filter(g => g.selector !== GroupBySelector.service_account_id);
        const denominatorSuffixHandler = nonServiceAccountGroupBy.length ? new K8BillingGroupByHandlerMulti(nonServiceAccountGroupBy) : null;

        return {
            isGroupBySA,
            accountToDetails,
            k8Handler,
            denominatorSuffixHandler
        };
    }

    public async computeK8TrendValues(k8TrendResult: BillingTrendResult[],
                                      billingTrendResult: BillingTrendResult[],
                                      fetcher: ServiceAccountFetcher,
                                      groupBy: BillingGroupBy[],
                                      domain: AppDomainContext): Promise<BillingK8sResultExtended[]>
    {
        k8TrendResult ||= [];
        billingTrendResult ||= [];

        const scopedCostContext: BillingK8TrendScopedCostComputationContext = {
            ...(await this.getK8ScopedCostComputationContext(k8TrendResult,
                                                             billingTrendResult,
                                                             fetcher,
                                                             groupBy,
                                                             domain)),
            denominatorByGroup: new Map<string, BillingTrendPoints>()
        };

        const getDenominatorKey = (identity: BillingLineItemExtended): string => this.getK8DenominatorKey(identity,
                                                                                                          scopedCostContext.isGroupBySA,
                                                                                                          scopedCostContext.accountToDetails,
                                                                                                          scopedCostContext.k8Handler,
                                                                                                          scopedCostContext.denominatorSuffixHandler);
        for (const billingTrend of billingTrendResult)
        {
            const key      = getDenominatorKey(billingTrend.identity);
            const existing = scopedCostContext.denominatorByGroup.get(key);
            scopedCostContext.denominatorByGroup.set(key, existing ? existing.merge(billingTrend.trend) : billingTrend.trend);
        }

        const result: BillingK8sResultExtended[] = [];
        for (const k8Trend of k8TrendResult)
        {
            const denominatorKey   = getDenominatorKey(k8Trend.identity);
            const denominatorTrend = scopedCostContext.denominatorByGroup.get(denominatorKey) || new BillingTrendPoints([]);
            const serviceAccountId = k8Trend?.identity?.model?.service_account_id;
            const resourceId       = scopedCostContext.isGroupBySA ? this.getNormalizedServiceAccountId(scopedCostContext.accountToDetails, serviceAccountId) : undefined;
            const numAccounts      = scopedCostContext.isGroupBySA ? 1 : 0;

            result.push(new BillingK8sResultExtended(k8Trend.identity,
                                                     numAccounts,
                                                     k8Trend.trend,
                                                     null,
                                                     denominatorTrend,
                                                     null,
                                                     resourceId));
        }

        return result;
    }

    public async computeK8AggregationValues(k8AggregationResult: BillingAggregationResult[],
                                            billingAggregationResult: BillingAggregationResult[],
                                            fetcher: ServiceAccountFetcher,
                                            groupBy: BillingGroupBy[],
                                            domain: AppDomainContext): Promise<BillingK8sAggregationResultExtended[]>
    {
        k8AggregationResult ||= [];
        billingAggregationResult ||= [];

        const scopedCostContext: BillingK8AggregationScopedCostComputationContext = {
            ...(await this.getK8ScopedCostComputationContext(k8AggregationResult,
                                                             billingAggregationResult,
                                                             fetcher,
                                                             groupBy,
                                                             domain)),
            denominatorByGroup: new Map<string, number>()
        };

        const getDenominatorKey = (identity: BillingLineItemExtended): string => this.getK8DenominatorKey(identity,
                                                                                                          scopedCostContext.isGroupBySA,
                                                                                                          scopedCostContext.accountToDetails,
                                                                                                          scopedCostContext.k8Handler,
                                                                                                          scopedCostContext.denominatorSuffixHandler);
        for (const billingAggregation of billingAggregationResult)
        {
            const key = getDenominatorKey(billingAggregation.identity);
            scopedCostContext.denominatorByGroup.set(key, (scopedCostContext.denominatorByGroup.get(key) || 0) + (billingAggregation.value || 0));
        }

        const result: BillingK8sAggregationResultExtended[] = [];
        for (const k8Aggregation of k8AggregationResult)
        {
            const denominatorKey   = getDenominatorKey(k8Aggregation.identity);
            const totalCloudSpend  = scopedCostContext.denominatorByGroup.get(denominatorKey) || 0;
            const scopedCost       = totalCloudSpend !== 0 ? k8Aggregation.value / totalCloudSpend : 0;
            const serviceAccountId = k8Aggregation?.identity?.model?.service_account_id;
            const resourceId       = scopedCostContext.isGroupBySA ? this.getNormalizedServiceAccountId(scopedCostContext.accountToDetails, serviceAccountId) : undefined;

            result.push(new BillingK8sAggregationResultExtended(k8Aggregation.identity,
                                                                k8Aggregation.value,
                                                                k8Aggregation.numAccounts,
                                                                totalCloudSpend,
                                                                scopedCost,
                                                                resourceId));
        }

        return result;
    }

    public aggregateMarginCostValues(contractedCostResult: BillingAggregationResultExtended[],
                                     sourceCostResult: BillingAggregationResultExtended[],
                                     billingSource: string,
                                     retailCostResult?: BillingAggregationResultExtended[]): BillingAggregationResult[]
    {
        const result: BillingMarginAggResultExtended[] = [];
        const hasSourceCost                            = sourceCostResult.length > 0;

        const retailCostMap = new Map<string, number>();
        for (const retailCost of retailCostResult || [])
        {
            retailCostMap.set(this.getKey(retailCost.identity), retailCost.value);
        }

        const contractedCostMap = new Map<string, BillingAggregationResultExtended>();
        for (const contracted of contractedCostResult)
        {
            const key = this.getKey(contracted.identity);
            contractedCostMap.set(key, contracted);

            if (!hasSourceCost)
            {
                const margin       = contracted.value;
                const profitMargin = contracted.value === 0 ? 0 : 100;
                result.push(new BillingMarginAggResultExtended(contracted.identity,
                                                               0,
                                                               contracted.value,
                                                               margin,
                                                               profitMargin,
                                                               contracted.numAccounts,
                                                               billingSource,
                                                               retailCostMap.get(key)));
            }
        }

        for (const sourceCost of sourceCostResult)
        {
            const key            = this.getKey(sourceCost.identity);
            const contractedCost = contractedCostMap.get(key);

            if (contractedCost)
            {
                const margin       = contractedCost.value - sourceCost.value;
                const profitMargin = PercentagePipe.computeSafely(margin, contractedCost.value);

                result.push(new BillingMarginAggResultExtended(sourceCost.identity,
                                                               sourceCost.value,
                                                               contractedCost.value,
                                                               margin,
                                                               profitMargin,
                                                               sourceCost.numAccounts,
                                                               billingSource,
                                                               retailCostMap.get(key)));
            }
        }
        return result;
    }

    public combinedGroupBy(disableImplicitServiceAccountGrouping?: boolean): { group_by: Models.BillingGroupByDetails[], skip_service_account_grouping: boolean }
    {
        const group_by                    = [];
        let skip_service_account_grouping = !!disableImplicitServiceAccountGrouping;
        for (const handler of this.m_handlers || [])
        {
            const curr = handler.getBillingGroupBy();
            if (curr)
            {
                group_by.push(curr);
                if (curr.selector === Models.BillingGroupBySelector.service_account_id ||
                    curr.selector === Models.BillingGroupBySelector.compartment_name ||
                    curr.selector === Models.BillingGroupBySelector.compartment_path ||
                    curr.selector === Models.BillingGroupBySelector.service_account_tags)
                {
                    skip_service_account_grouping = false;
                }
            }
        }

        return {
            skip_service_account_grouping,
            group_by
        };
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        const parts = this.m_handlers.map((handler) => handler.getKey(identity));
        return parts.join("/");
    }

    protected getIdentity(key: string,
                          identity: BillingLineItemExtended): BillingLineItemExtended
    {
        return identity;
    }
}

class K8BillingGroupByHandlerMulti extends BillingGroupByHandlerMulti
{
    private readonly m_denominatorKeyHandler: BillingGroupByHandlerMulti;

    constructor(private readonly m_groupBy: BillingGroupBy[])
    {
        super(m_groupBy);

        const denominatorGroupBy = [];
        for (const groupBy of m_groupBy || [])
        {
            switch (groupBy.selector)
            {
                case GroupBySelector.extras:
                case GroupBySelector.pods:
                case GroupBySelector.resource_type:
                case GroupBySelector.utilization_type:
                case GroupBySelector.workload_type:
                    break;

                default:
                    denominatorGroupBy.push(groupBy);
                    break;
            }
        }

        this.m_denominatorKeyHandler = denominatorGroupBy.length ? new BillingGroupByHandlerMulti(denominatorGroupBy) : null;
    }

    public getDenominatorKey(identity: BillingLineItemExtended): string
    {
        return this.m_denominatorKeyHandler?.getKey(identity) || "__all__";
    }
}

class AvailabilityZonesBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.availability_zone>
{
    public readonly billingGroupBy = GroupBySelector.availability_zone;
}

class BillingEntityBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.billing_entity>
{
    public readonly billingGroupBy = GroupBySelector.billing_entity;
}

class UtilizationTypeBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.utilization_type>
{
    public readonly billingGroupBy = GroupBySelector.utilization_type;
}

class ChargeTypeBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.line_item_type>
{
    public readonly billingGroupBy = GroupBySelector.line_item_type;
}

class ResourceTypeGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.resource_type>
{
    public readonly billingGroupBy = GroupBySelector.resource_type;
}

class CloudAccountBillingGroupByHandler extends BillingGroupByHandler
{
    public getKey(identity: BillingLineItemExtended): string
    {
        return identity.model.service_account_id;
    }

    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        // Cloud provider is resolved client-side from account context, so keep SA granularity in the backend response.
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.service_account_id
                                                        });
    }
}

class CloudAccountTagBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.service_account_tags,
                                                            key     : this.m_groupBy.key
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        const tags = identity.cloudAccount?.tags;
        if (this.m_groupBy.key && tags)
        {
            return tags.find(t => t.key === this.m_groupBy.key)?.value;
        }

        return null;
    }
}

class CloudProviderBillingGroupByHandler extends BillingGroupByHandler
{
    public getKey(identity: BillingLineItemExtended): string
    {
        return CloudProvidersService.isAzure(identity.cloud) ? Models.CloudServiceName.Azure : identity.cloud;
    }

    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.service_account_id
                                                        });
    }
}

class CompartmentBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.compartment_name
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        return (identity.model as Models.BillingLineItemIdentity)?.compartment_name || null;
    }
}

class CompartmentPathBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.compartment_path
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        return (identity.model as Models.BillingLineItemIdentity)?.compartment_path || null;
    }
}

class MeterSubCategoryBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.meter_sub_category>
{
    public readonly billingGroupBy = GroupBySelector.meter_sub_category;
}

class PricingCategoryBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.pricing_category>
{
    public readonly billingGroupBy = GroupBySelector.pricing_category;
}

class PricingPurchaseOptionBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.pricing_purchase_option>
{
    public readonly billingGroupBy = GroupBySelector.pricing_purchase_option;
}

class ProductCategoryBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.product_category>
{
    public readonly billingGroupBy = GroupBySelector.product_category;
}

class ProductFamilyBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.product_family>
{
    public readonly billingGroupBy = GroupBySelector.product_family;
}

class ProductServiceCodeBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.product_service_code>
{
    public readonly billingGroupBy = GroupBySelector.product_service_code;
}

class ProductSkuBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.product_sku>
{
    public readonly billingGroupBy = GroupBySelector.product_sku;
}

class PublisherBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.publisher>
{
    public readonly billingGroupBy = GroupBySelector.publisher;
}

class RegionBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.location>
{
    public readonly billingGroupBy = GroupBySelector.location;
}

class ResourceCategoryBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.resource_category>
{
    public readonly billingGroupBy = GroupBySelector.resource_category;
}

class ResourceExtrasBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.extras,
                                                            key     : this.m_groupBy.key
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        const extras = identity?.model?.extras as Models.KeyValuePair[] | undefined;
        if (!extras?.length) return "extras:none";

        const parts: string[] = [];

        for (const e of extras)
        {
            if (e?.key)
            {
                parts.push(`${e.key}=${e.value ?? ""}`);
            }
        }

        parts.sort();

        return "extras:" + parts.join("|");
    }
}

class ResourceGroupBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.resource_group>
{
    public readonly billingGroupBy = GroupBySelector.resource_group;
}

class ResourceIdBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.resource_id>
{
    public readonly billingGroupBy = GroupBySelector.resource_id;
}

class ResourceNameBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.resource_name>
{
    public readonly billingGroupBy = GroupBySelector.resource_name;
}

class ResourceOperationBillingGroupByHandler extends BillingGroupBySimpleHandler<GroupBySelector.resource_operation>
{
    public readonly billingGroupBy = GroupBySelector.resource_operation;
}

class ResourceTagBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.tags,
                                                            key     : this.m_groupBy.key
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        const tags = identity?.model?.tags as Models.KeyValuePair[] | undefined;
        if (!tags?.length) return "tags:none";

        const parts: string[] = [];

        for (const e of tags)
        {
            if (e?.key)
            {
                parts.push(`${e.key}=${e.value ?? ""}`);
            }
        }

        parts.sort();

        return "tags:" + parts.join("|");
    }
}

class K8sWorkloadTypeBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.extras,
                                                            key     : this.m_groupBy.key
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        return identity.model.extras?.[0]?.key;
    }

    public static filterWorkloadsByPrecedence(extras: Models.KeyValuePair[]): Models.KeyValuePair[]
    {
        let hasDeployment = false;
        let hasCronJob    = false;

        for (const extra of extras)
        {
            const key = extra.key?.toLowerCase();
            if (key === KubernetesWorkloadType.Deployment) hasDeployment = true;
            if (key === KubernetesWorkloadType.CronJob) hasCronJob = true;
        }

        return extras.filter(e =>
                             {
                                 const key = e.key?.toLowerCase();
                                 if (key === KubernetesWorkloadType.Replicaset && hasDeployment) return false;
                                 if (key === KubernetesWorkloadType.Job && hasCronJob) return false;
                                 return true;
                             });
    }
}

class ResourceTagKeysBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.tags
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        const resourceTags = identity?.model?.tags as Models.KeyValuePair[] | undefined;
        if (!resourceTags?.length) return "resourcetags:none";

        const parts: string[] = [];

        for (const e of resourceTags)
        {
            if (e?.key)
            {
                parts.push(`${e.key}=${e.value ?? ""}`);
            }
        }

        parts.sort();

        return "resourcetags:" + parts.join("|");
    }
}

class PodsBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.extras,
                                                            key     : Models.KubernetesWorkload.pod
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        const pods = identity?.model?.extras as Models.KeyValuePair[] | undefined;
        if (!pods?.length) return "pods:none";

        const parts: string[] = [];

        for (const e of pods)
        {
            if (e?.key)
            {
                parts.push(`${e.key}=${e.value ?? ""}`);
            }
        }

        parts.sort();

        return "pods:" + parts.join("|");
    }
}

class TenantBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        // Tenant name/id is derived client-side from account context, so keep SA granularity in the backend response.
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.service_account_id
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        return identity.tenantId;
    }
}

class TenantTagBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.tenant_tags,
                                                            key     : this.m_groupBy.key
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        const tags = identity.tenantTags;
        if (this.m_groupBy.key && tags)
        {
            return tags.find(t => t.key === this.m_groupBy.key)?.value;
        }

        return null;
    }
}

class HierarchyBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.hierarchy,
                                                            key     : this.m_groupBy.key
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        const hierarchy = identity.hierarchy;
        if (this.m_groupBy.key && hierarchy)
        {
            return hierarchy.find(t => t.key === this.m_groupBy.key)?.value;
        }

        return null;
    }
}

class DimensionBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.dimension,
                                                            key     : this.m_groupBy.key,
                                                            groups  : this.m_groupBy.groups
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        const custom = identity.custom;
        if (this.m_groupBy.key && custom)
        {
            return custom.find(t => t.key === this.m_groupBy.key)?.value;
        }

        return null;
    }
}

class K8TagsBillingGroupByHandler extends BillingGroupByHandler
{
    public getBillingGroupBy(): Models.BillingGroupByDetails
    {
        return Models.BillingGroupByDetails.newInstance({
                                                            selector: Models.BillingGroupBySelector.tags,
                                                            key     : this.m_groupBy.key,
                                                            kind    : this.m_groupBy.kind
                                                        });
    }

    public getKey(identity: BillingLineItemExtended): string
    {
        return identity.model.tags?.[0]?.value;
    }
}

const handlers: Lookup<Type<BillingGroupByHandler>, GroupBySelector> = {
    [GroupBySelector.account_type]           : CloudAccountBillingGroupByHandler,
    [GroupBySelector.availability_zone]      : AvailabilityZonesBillingGroupByHandler,
    [GroupBySelector.billing_entity]         : BillingEntityBillingGroupByHandler,
    [GroupBySelector.cloud_account_hierarchy]: HierarchyBillingGroupByHandler,
    [GroupBySelector.compartment_name]       : CompartmentBillingGroupByHandler,
    [GroupBySelector.compartment_path]       : CompartmentPathBillingGroupByHandler,
    [GroupBySelector.dimension]              : DimensionBillingGroupByHandler,
    [GroupBySelector.extras]                 : ResourceExtrasBillingGroupByHandler,
    [GroupBySelector.k8Tags]                 : K8TagsBillingGroupByHandler,
    [GroupBySelector.line_item_type]         : ChargeTypeBillingGroupByHandler,
    [GroupBySelector.location]               : RegionBillingGroupByHandler,
    [GroupBySelector.meter_sub_category]     : MeterSubCategoryBillingGroupByHandler,
    [GroupBySelector.pods]                   : PodsBillingGroupByHandler,
    [GroupBySelector.pricing_category]       : PricingCategoryBillingGroupByHandler,
    [GroupBySelector.pricing_purchase_option]: PricingPurchaseOptionBillingGroupByHandler,
    [GroupBySelector.product_category]       : ProductCategoryBillingGroupByHandler,
    [GroupBySelector.product_family]         : ProductFamilyBillingGroupByHandler,
    [GroupBySelector.product_service_code]   : ProductServiceCodeBillingGroupByHandler,
    [GroupBySelector.product_sku]            : ProductSkuBillingGroupByHandler,
    [GroupBySelector.publisher]              : PublisherBillingGroupByHandler,
    [GroupBySelector.resource_category]      : ResourceCategoryBillingGroupByHandler,
    [GroupBySelector.resource_group]         : ResourceGroupBillingGroupByHandler,
    [GroupBySelector.resource_id]            : ResourceIdBillingGroupByHandler,
    [GroupBySelector.resource_name]          : ResourceNameBillingGroupByHandler,
    [GroupBySelector.resource_operation]     : ResourceOperationBillingGroupByHandler,
    [GroupBySelector.resource_type]          : ResourceTypeGroupByHandler,
    [GroupBySelector.service_account_id]     : CloudAccountBillingGroupByHandler,
    [GroupBySelector.service_account_tags]   : CloudAccountTagBillingGroupByHandler,
    [GroupBySelector.service_name]           : CloudProviderBillingGroupByHandler,
    [GroupBySelector.tag_keys]               : ResourceTagKeysBillingGroupByHandler,
    [GroupBySelector.tag_status]             : CloudAccountBillingGroupByHandler,
    [GroupBySelector.tags]                   : ResourceTagBillingGroupByHandler,
    [GroupBySelector.tenant_id]              : TenantBillingGroupByHandler,
    [GroupBySelector.tenant_tags]            : TenantTagBillingGroupByHandler,
    [GroupBySelector.utilization_type]       : UtilizationTypeBillingGroupByHandler,
    [GroupBySelector.workload_type]          : K8sWorkloadTypeBillingGroupByHandler
};

interface BillingTrendDataInternal
{
    values: BillingTrendResult[];
    startTime: number;
    endTime: number;

    forecastEndTime?: number;
    currGranularityActual?: BillingTrendResultExtended[];
}

/**
 * Everything `costTrend` resolves before the fetch. Shared by the one-shot and streaming paths so the
 * two cannot build different requests.
 */
interface CostTrendPlan
{
    request: Models.BillingAggregationTrendRequest;
    notForecastedRequest: Models.BillingAggregationTrendRequest;
    isBillingRangeMonth: boolean;
    startTime: number;
    endTime: number;
    forecastEndTime: number;

    /**
     * A group-by that asks for nothing. No request is issued and the result is empty.
     */
    degenerate: boolean;
}

/**
 * Everything `costAndUsageTrend` resolves before the fetch, minus the billing source (see
 * `stampCostAndUsageBillingSource`). `marginRate`'s (up to three) parallel legs differ only in
 * billing source, so the plan is built once and stamped per source.
 */
interface CostAndUsageTrendPlan
{
    request: Models.BillingCostAndUsageAggregationRequest;
    isBillingRangeMonth: boolean;
    startTime: number;
    endTime: number;
}

/**
 * The row-independent half of the post-process tail, resolved once so the tail can run synchronously
 * against a growing accumulator.
 */
interface TrendPostProcessContext
{
    groupBy: BillingGroupBy[];
    granularity: Models.TrendGranularity;
    billingSource: string;
    currency: Models.Currency;
    context: Models.DataContext;
    rangeExt: RangeSelectionExtended;

    isBillingSourceMargin: boolean;
    platformAsKubernetes: boolean;
    fillEmptyTrendPoints: boolean;
    hasForecastOrActuals: boolean;
    rollUpToYearly: boolean;

    fiscalStartMonth: number;
    forecastSplitTime: number;
    forecastTrimStartTime: number;

    handler: BillingGroupByHandlerMulti;
    resolvedFilterOptions: FilterOptionsExtended;
    currencyConverter: CurrencyConverter;

    restrictToServiceAccountIds: string[];
}

interface BillingGroupProcessingResult
{
    query: Models.QueryOperator;
    hasTenantTags: boolean;
    hasServiceAccountTags: boolean;
    groupByResourceTagKeys: boolean;
    groupByKubernetesPod: boolean;
}

interface BillingK8ScopedCostComputationContextBase
{
    isGroupBySA: boolean;
    accountToDetails: Lookup<Models.ServiceAccountDetails>;
    k8Handler: K8BillingGroupByHandlerMulti;
    denominatorSuffixHandler: K8BillingGroupByHandlerMulti | null;
}

interface BillingK8TrendScopedCostComputationContext extends BillingK8ScopedCostComputationContextBase
{
    denominatorByGroup: Map<string, BillingTrendPoints>;
}

interface BillingK8AggregationScopedCostComputationContext extends BillingK8ScopedCostComputationContextBase
{
    denominatorByGroup: Map<string, number>;
}

export interface BillingAggregationResult
{
    identity: BillingLineItemExtended;
    numAccounts: number;
    value: number;
    cloud: Models.CloudServiceName;
    serviceAccountId: string;
    serviceAccountName: string;

    sourceCost?: number;
    margin?: number;
    contractedCost?: number;
    profitMargin?: number;
    retailCost?: number;

    totalCloudSpend?: number;
    scopedCost?: number;
    resourceServiceAccountId?: string;
}

interface BillingTrendInterface
{
    identity: Models.BillingLineItemIdentityPlus;
    timestamps: Array<Date>;
    values: number[];
    descriptions?: string[];
    rates?: number[];
    usages?: number[];
    billedRates?: number[];

    sourceCost?: number;
    margin?: number;
    contractedCost?: number;
    profitMargin?: number;

    scopedCost?: number;
}

export enum GroupByIdentifierFriendlyName
{
    Uncategorized       = "Uncategorized",
    Null                = "NULL",
    NoResourceGroup     = "No Resource Group",
    OtherCategories     = "Other Categories",
    PreviousPeriodSpend = "Previous period spend",
    Others              = "Others",
    Average             = "Average"
}

export interface DimensionData
{
    dimensionId: string;
    groupingRuleId: string;
}

export interface K8ScopedTrendPoint
{
    id: string,
    points: BillingTrendPoints
}

export interface K8ScopedPoints
{
    id: string,
    points: BillingTrendPoint[]
}
